import React, { useState, useMemo, useEffect } from 'react';
import { Container, Card, Button, Badge, Form, InputGroup, Dropdown, Modal, Spinner, Alert, Row, Col, Table, OverlayTrigger, Tooltip, Offcanvas, Tabs, Tab, ListGroup } from 'react-bootstrap';
import {
  useGetDealsQuery,
  useCreateDealMutation,
  useUpdateDealMutation,
  useDeleteDealMutation,
  useGetUserPreferencesQuery,
  useUpdateUserPreferencesMutation,
  useGetContactsQuery,
  useGetLeadsQuery,
  useGetClientsQuery,
  useUpdateLeadMutation,
  useUpdateClientMutation,
  useUpdateContactMutation,
  useGetEmailsQuery,
  useCreateClientMutation,
  useGetRulesEngineConfigQuery,
} from '../services/api';
import { DEFAULT_RULES_ENGINE_CONFIG, normalizeRulesEngineConfig } from '../constants/rulesEngine';
import { useUser } from '../contexts/UserContext';
import {
  preferencesTenantWriteSpread,
  useTenantIdForPreferences,
  useUserPreferencesQueryArg,
} from '../hooks/useTenantIdForPreferences';
import { buildEmailThreadsForEntity, mergeEmailResponses } from '../utils/crmEmailThreads';
import {
  getTenantTimezoneFromPreferencesData,
  toLocaleDateStringInTimezone,
} from '../utils/tenantTimezone';
import { mergeLabelCatalogCandidates } from '../utils/crmLabels';
import { 
  Search, 
  Plus, 
  ChevronDown, 
  X,
  CurrencyDollar,
  Calendar,
  Briefcase,
  Building,
  BuildingFill,
  Person,
  PeopleFill,
  CheckSquare,
  Phone,
  Tag,
  Dot,
  ArrowRight,
  GeoAltFill,
  Globe2,
  Linkedin,
  FileText,
  Pencil,
  GripVertical,
  ArrowUp,
  ArrowDown,
  List,
  Kanban,
  Eye,
  Gear,
  Trash2,
  InfoCircle,
  Check,
  XLg,
  Telephone,
  Envelope
} from 'react-bootstrap-icons';

interface Deal {
  id: string;
  name: string;
  value: string;
  currency?: string;
  stage?: string;
  probability?: number;
  expectedCloseDate?: string;
  actualCloseDate?: string;
  clientId?: string;
  contactId?: string;
  leadId?: string;
  owner?: string;
  visibility?: 'global' | 'personal';
  sourceOrigin?: string;
  sourceChannel?: string;
  sourceChannelId?: string;
  businessUnit?: string;
  inStore?: boolean;
  notes?: string;
  tags?: string[];
  createdAt?: string;
  updatedAt?: string;
}

interface PipelineStage {
  id: string;
  name: string;
  probability: number;
  rottingDays?: number;
}

interface Pipeline {
  id: string;
  name: string;
  stages: PipelineStage[];
  dealProbability: boolean;
  order: number;
}

type OrganizationOption = {
  id: string;
  type: 'client' | 'contactOrg' | 'leadOrg';
  label: string;
};

type DealEditableFieldId =
  | 'name'
  | 'email'
  | 'phone'
  | 'value'
  | 'stage'
  | 'contactStage'
  | 'expectedCloseDate'
  | 'businessUnit'
  | 'sourceOrigin'
  | 'sourceChannel'
  | 'sourceChannelId'
  | 'owner'
  | 'visibility'
  | 'organization'
  | 'accountStage'
  | 'address'
  | 'website'
  | 'linkedInProfile'
  | 'numberOfEmployees'
  | 'annualRevenue'
  | 'industry';

type LabelsFieldId = 'detailsLabels';

type DealEditableFieldConfig = {
  inputType: 'text' | 'date';
  placeholder: string;
};

type DealSidebarTarget = {
  type: 'deal' | 'lead' | 'contact' | 'client';
  id: string;
  record?: any;
};

type DragAction = 'delete' | 'lost' | 'won' | 'move';
type DealSidebarTab = 'notes' | 'activity' | 'email' | 'files';

const DEAL_EDITABLE_FIELD_CONFIG: Record<DealEditableFieldId, DealEditableFieldConfig> = {
  name: { inputType: 'text', placeholder: 'No name' },
  email: { inputType: 'text', placeholder: 'No email' },
  phone: { inputType: 'text', placeholder: 'No phone' },
  value: { inputType: 'text', placeholder: '$ Value' },
  stage: { inputType: 'text', placeholder: 'Stage' },
  contactStage: { inputType: 'text', placeholder: 'Contact Stage' },
  expectedCloseDate: { inputType: 'date', placeholder: 'Expected close date' },
  businessUnit: { inputType: 'text', placeholder: 'Business Unit' },
  sourceOrigin: { inputType: 'text', placeholder: 'Source origin' },
  sourceChannel: { inputType: 'text', placeholder: 'Source channel' },
  sourceChannelId: { inputType: 'text', placeholder: 'Source channel ID' },
  owner: { inputType: 'text', placeholder: 'Owner' },
  visibility: { inputType: 'text', placeholder: 'Visibility' },
  organization: { inputType: 'text', placeholder: 'No organization' },
  accountStage: { inputType: 'text', placeholder: 'Account Stage' },
  address: { inputType: 'text', placeholder: 'Address' },
  website: { inputType: 'text', placeholder: 'Website' },
  linkedInProfile: { inputType: 'text', placeholder: 'LinkedIn profile' },
  numberOfEmployees: { inputType: 'text', placeholder: 'Number of employees' },
  annualRevenue: { inputType: 'text', placeholder: 'Annual revenue' },
  industry: { inputType: 'text', placeholder: 'Industry' },
};

const DEFAULT_OWNER_FILTER_LABEL = 'Everyone';
const DEFAULT_SELF_OWNER_LABEL = 'PhiLL Kross';

const dealSidebarIconStyle: React.CSSProperties = {
  color: '#6c757d',
  width: 16,
  display: 'inline-flex',
  justifyContent: 'center',
  flexShrink: 0,
};

const getSharedLabelColors = (): Record<string, string> => {
  const defaultColors: Record<string, string> = {
    HOT: 'danger',
    COLD: 'primary',
    WARM: 'warning',
    'QUARTER 2': 'secondary',
    'QUARTER 1': 'info',
    'QUARTER 3': 'info',
    'QUARTER 4': 'warning',
    NTTM: 'info',
    FRANCHISE: 'success',
    '1-10 LOCATIONS': 'warning',
    CHAINS: 'success',
    'I.P.M.': 'secondary'
  };

  try {
    const stored = localStorage.getItem('labelColors');
    if (stored) {
      const storedColors = JSON.parse(stored);
      return { ...defaultColors, ...storedColors };
    }
  } catch {
    // Ignore storage errors and use default colors.
  }

  return defaultColors;
};

const DEFAULT_STAGES: PipelineStage[] = [
  { id: 'prospecting', name: 'Prospecting', probability: 10, rottingDays: 30 },
  { id: 'qualification', name: 'Qualification', probability: 25, rottingDays: 14 },
  { id: 'proposal', name: 'Proposal', probability: 50, rottingDays: 7 },
  { id: 'negotiation', name: 'Negotiation', probability: 75, rottingDays: 7 },
  { id: 'closed-won', name: 'Closed Won', probability: 100 },
  { id: 'closed-lost', name: 'Closed Lost', probability: 0 },
];

const DEFAULT_PIPELINES: Pipeline[] = [
  { id: 'default', name: 'Sales Pipeline', stages: DEFAULT_STAGES, dealProbability: true, order: 0 },
];

const normalizeId = (s: string) => String(s || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const isDateInputValue = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);
const parseDealNameParts = (value?: string): { primary: string; organization: string } => {
  const raw = String(value || '').trim();
  if (!raw) return { primary: '', organization: '' };
  const parts = raw.split(/\s[-–—]\s/).map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return {
      primary: parts[0],
      organization: parts.slice(1).join(' - '),
    };
  }
  return { primary: raw, organization: '' };
};
const toDateInputValue = (value?: string): string => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (isDateInputValue(text)) return text;
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return '';
  return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, '0')}-${String(parsed.getUTCDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const sanitizePipelines = (raw: unknown): Pipeline[] => {
  if (!Array.isArray(raw)) return DEFAULT_PIPELINES;
  const result: Pipeline[] = [];
  const seen = new Set<string>();
  raw.forEach((item: any, idx: number) => {
    if (!item || typeof item !== 'object') return;
    const name = String(item.name || '').trim();
    if (!name) return;
    const id = normalizeId(item.id || name) || `pipeline-${idx}`;
    if (seen.has(id)) return;
    seen.add(id);
    const stages = Array.isArray(item.stages) ? item.stages.map((s: any, i: number) => ({
      id: String(s?.id || `stage-${i}`).trim() || `stage-${i}`,
      name: String(s?.name || '').trim() || `Stage ${i + 1}`,
      probability: typeof s?.probability === 'number' ? s.probability : 50,
      rottingDays: typeof s?.rottingDays === 'number' ? s.rottingDays : undefined,
    })) : DEFAULT_STAGES;
    result.push({
      id,
      name,
      stages: stages.length ? stages : DEFAULT_STAGES,
      dealProbability: item.dealProbability !== false,
      order: typeof item.order === 'number' ? item.order : idx,
    });
  });
  result.sort((a, b) => a.order - b.order);
  return result.length ? result : DEFAULT_PIPELINES;
};

const Deals: React.FC = () => {
  const { currentUser } = useUser();
  const { data: dealsData, isLoading: isLoadingDeals, error: dealsError, refetch: refetchDeals } = useGetDealsQuery({});
  const [createDeal, { isLoading: isCreatingDeal }] = useCreateDealMutation();
  const [updateDeal, { isLoading: isUpdatingDeal }] = useUpdateDealMutation();
  const [updateLead, { isLoading: isUpdatingLead }] = useUpdateLeadMutation();
  const [updateClient, { isLoading: isUpdatingClient }] = useUpdateClientMutation();
  const [updateContact, { isLoading: isUpdatingContact }] = useUpdateContactMutation();
  const [deleteDeal] = useDeleteDealMutation();
  const [createClient] = useCreateClientMutation();
  const { data: rulesEngineConfigData } = useGetRulesEngineConfigQuery(undefined);
  const tenantIdForPrefs = useTenantIdForPreferences();
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );
  const [updateUserPreferences] = useUpdateUserPreferencesMutation();
  const [showAddDealModal, setShowAddDealModal] = useState(false);
  const { data: contactsData, refetch: refetchContacts } = useGetContactsQuery({});
  const { data: leadsData, refetch: refetchLeads } = useGetLeadsQuery({});
  const { data: clientsData, refetch: refetchClients } = useGetClientsQuery({});
  
  const deals = dealsData || [];
  const contacts = contactsData || [];
  const leads = leadsData || [];
  const clients = clientsData || [];
  const rulesEngineConfig = useMemo(
    () => normalizeRulesEngineConfig(rulesEngineConfigData || DEFAULT_RULES_ENGINE_CONFIG),
    [rulesEngineConfigData]
  );
  const accountStageOptions = rulesEngineConfig.accountStages;
  const contactStageOptions = rulesEngineConfig.contactStages;
  const selfOwnerBaseLabel = useMemo(() => {
    const explicitName = String((currentUser as any)?.name || '').trim();
    const combinedName = `${String((currentUser as any)?.firstName || '').trim()} ${String((currentUser as any)?.lastName || '').trim()}`.trim();
    return explicitName || combinedName || DEFAULT_SELF_OWNER_LABEL;
  }, [currentUser]);
  const clientMap = useMemo(() => new Map(clients.map((item: any) => [item.id, item])), [clients]);
  const contactMap = useMemo(() => new Map(contacts.map((item: any) => [item.id, item])), [contacts]);
  const leadMap = useMemo(() => new Map(leads.map((item: any) => [item.id, item])), [leads]);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [orgSearch, setOrgSearch] = useState('');
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [draggedDeal, setDraggedDeal] = useState<Deal | null>(null);
  const [dragActionHover, setDragActionHover] = useState<DragAction | null>(null);
  const [wonConfettiPieces, setWonConfettiPieces] = useState<Array<{
    id: string;
    left: number;
    delay: number;
    duration: number;
    rotate: number;
    color: string;
    size: number;
  }>>([]);
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');
  const [selectedPipelineId, setSelectedPipelineId] = useState<string>('default');
  const [pipelines, setPipelines] = useState<Pipeline[]>(DEFAULT_PIPELINES);
  const [showPipelineDropdown, setShowPipelineDropdown] = useState(false);
  const [showEditPipelineModal, setShowEditPipelineModal] = useState(false);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState<Pipeline | null>(null);
  const [reorderPipelines, setReorderPipelines] = useState<Pipeline[]>([]);
  const [addDealPipelineId, setAddDealPipelineId] = useState('');
  const [showDealSidebar, setShowDealSidebar] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [dealSidebarTab, setDealSidebarTab] = useState<DealSidebarTab>('notes');
  const [isSavingSidebarDeal, setIsSavingSidebarDeal] = useState(false);
  const [showAllDealDetailFields, setShowAllDealDetailFields] = useState(false);
  const [showAllDealPersonFields, setShowAllDealPersonFields] = useState(false);
  const [showAllDealOrganizationFields, setShowAllDealOrganizationFields] = useState(false);
  const [dealSidebarHoveredField, setDealSidebarHoveredField] = useState<DealEditableFieldId | LabelsFieldId | null>(null);
  const [dealSidebarEditingFieldId, setDealSidebarEditingFieldId] = useState<DealEditableFieldId | null>(null);
  const [dealSidebarFieldDraftValue, setDealSidebarFieldDraftValue] = useState('');
  const [showLabelsDropdownField, setShowLabelsDropdownField] = useState<LabelsFieldId | null>(null);
  const [labelSearchQuery, setLabelSearchQuery] = useState('');
  const [customLabels, setCustomLabels] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('customLabels');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [dealSidebarForm, setDealSidebarForm] = useState({
    name: '',
    value: '',
    currency: 'US Dollar (USD)',
    stage: '',
    probability: 0,
    expectedCloseDate: '',
    clientId: '',
    notes: '',
  });

  useEffect(() => {
    const raw = (userPreferencesData as any)?.preferences?.crmPipelines;
    const sanitized = sanitizePipelines(raw);
    setPipelines(sanitized);
    setSelectedPipelineId((prev) => {
      const exists = sanitized.some((p) => p.id === prev);
      return exists ? prev : (sanitized[0]?.id || 'default');
    });
  }, [userPreferencesData]);

  const persistPipelines = async (next: Pipeline[]) => {
    const withOrder = next.map((p, i) => ({ ...p, order: i }));
    try {
      await updateUserPreferences({
        ...preferencesTenantWriteSpread(tenantIdForPrefs),
        preferences: { crmPipelines: withOrder },
      }).unwrap();
      setPipelines(sanitizePipelines(withOrder));
    } catch (e: any) {
      console.error('Failed to save pipelines:', e);
      alert(`Failed to save pipelines: ${e?.data?.message || e?.message || 'Unknown error'}`);
    }
  };

  const selectedPipeline = useMemo(() => pipelines.find((p) => p.id === selectedPipelineId) || pipelines[0], [pipelines, selectedPipelineId]);
  const dealStages = selectedPipeline?.stages || DEFAULT_STAGES;
  const normalizeText = (value?: string | null): string => String(value || '').trim().toLowerCase();
  const isWonStage = (stageId?: string): boolean => {
    const stage = dealStages.find((entry) => entry.id === stageId);
    const stageText = `${stageId || ''} ${stage?.name || ''}`.toLowerCase();
    return stageText.includes('won') && !stageText.includes('lost');
  };
  const wonStageId = useMemo(() => {
    return (
      dealStages.find((entry) => {
        const stageText = `${entry.id || ''} ${entry.name || ''}`.toLowerCase();
        return stageText.includes('won') && !stageText.includes('lost');
      })?.id || ''
    );
  }, [dealStages]);
  const lostStageId = useMemo(() => {
    return (
      dealStages.find((entry) => {
        const stageText = `${entry.id || ''} ${entry.name || ''}`.toLowerCase();
        return stageText.includes('lost');
      })?.id || ''
    );
  }, [dealStages]);
  const triggerWonConfetti = () => {
    const colors = ['#0d6efd', '#198754', '#ffc107', '#fd7e14', '#dc3545', '#6f42c1'];
    const pieces = Array.from({ length: 72 }, (_, index) => ({
      id: `${Date.now()}-${index}`,
      left: Math.random() * 100,
      delay: Math.random() * 0.35,
      duration: 1.9 + Math.random() * 1.4,
      rotate: -45 + Math.random() * 90,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: 6 + Math.floor(Math.random() * 6),
    }));
    setWonConfettiPieces(pieces);
  };

  const addDealPipeline = useMemo(() => {
    const id = addDealPipelineId || selectedPipelineId || pipelines[0]?.id;
    return pipelines.find((p) => p.id === id) || pipelines[0];
  }, [pipelines, addDealPipelineId, selectedPipelineId]);
  const addDealStages = addDealPipeline?.stages || DEFAULT_STAGES;

  useEffect(() => {
    if (showAddDealModal) {
      const pipeId = selectedPipelineId || pipelines[0]?.id || '';
      setAddDealPipelineId(pipeId);
      setContactSearch('');
      setOrgSearch('');
      setShowContactDropdown(false);
      setShowOrgDropdown(false);
      const pipe = pipelines.find((p) => p.id === pipeId);
      const firstStage = pipe?.stages?.[0]?.id || 'prospecting';
      setFormData({
        name: '',
        value: '',
        currency: 'US Dollar (USD)',
        stage: firstStage,
        probability: 0,
        expectedCloseDate: '',
        clientId: '',
        contactId: '',
        leadId: '',
        contactName: '',
        personPhone: '',
        personEmail: '',
        notes: '',
        tags: ''
      });
    }
  }, [showAddDealModal, selectedPipelineId, pipelines]);

  useEffect(() => {
    if (!wonConfettiPieces.length) return undefined;
    const timeout = setTimeout(() => {
      setWonConfettiPieces([]);
    }, 2800);
    return () => clearTimeout(timeout);
  }, [wonConfettiPieces]);

  const [formData, setFormData] = useState({
    name: '',
    value: '',
    currency: 'US Dollar (USD)',
    stage: '',
    probability: 0,
    expectedCloseDate: '',
    clientId: '',
    contactId: '',
    leadId: '',
    contactName: '',
    personPhone: '',
    personEmail: '',
    notes: '',
    tags: ''
  });

  const filteredContactsAndLeads = useMemo(() => {
    const q = contactSearch.toLowerCase().trim();
    const contactItems = (contacts as { id: string; firstName?: string; lastName?: string; organization?: string }[]).map((c) => ({
      id: c.id,
      type: 'contact' as const,
      label: c.organization ? `${(c.firstName || '')} ${(c.lastName || '')}`.trim() + ` (${c.organization})` : `${(c.firstName || '')} ${(c.lastName || '')}`.trim() || 'Untitled contact',
      searchText: `${(c.firstName || '')} ${(c.lastName || '')} ${c.organization || ''}`.toLowerCase(),
    }));
    const leadItems = (leads as { id: string; name?: string; organization?: string }[]).map((l) => ({
      id: l.id,
      type: 'lead' as const,
      label: l.organization ? `${l.name || 'Untitled'} (${l.organization})` : (l.name || 'Untitled lead'),
      searchText: `${l.name || ''} ${l.organization || ''}`.toLowerCase(),
    }));
    const all = [...contactItems, ...leadItems];
    if (!q) return all.slice(0, 12);
    return all.filter((item) => item.searchText.includes(q)).slice(0, 12);
  }, [contacts, leads, contactSearch]);

  const contactDisplayLabel = useMemo(() => {
    if (formData.contactId) {
      const c = (contacts as { id: string; firstName?: string; lastName?: string; organization?: string }[]).find((x) => x.id === formData.contactId);
      if (c) {
        const name = `${(c.firstName || '')} ${(c.lastName || '')}`.trim();
        return c.organization ? `${name} (${c.organization})` : name || 'Contact';
      }
    }
    if (formData.leadId) {
      const l = (leads as { id: string; name?: string; organization?: string }[]).find((x) => x.id === formData.leadId);
      return l ? (l.organization ? `${l.name || ''} (${l.organization})` : (l.name || 'Lead')) : 'Lead';
    }
    return formData.contactName || '';
  }, [formData.contactId, formData.leadId, formData.contactName, contacts, leads]);

  const filteredOrganizations = useMemo((): OrganizationOption[] => {
    const q = orgSearch.toLowerCase().trim();
    const seen = new Set<string>();
    const list: OrganizationOption[] = [];

    const addOption = (option: OrganizationOption): void => {
      const key = option.label.trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      list.push(option);
    };

    (clients as { id: string; name?: string; organization?: string }[]).forEach((client) => {
      const label = String(client.name || client.organization || '').trim();
      if (!label) return;
      addOption({ id: client.id, type: 'client', label });
    });

    (contacts as { id: string; organization?: string }[]).forEach((contact) => {
      const label = String(contact.organization || '').trim();
      if (!label) return;
      addOption({ id: contact.id, type: 'contactOrg', label });
    });

    (leads as { id: string; organization?: string }[]).forEach((lead) => {
      const label = String(lead.organization || '').trim();
      if (!label) return;
      addOption({ id: lead.id, type: 'leadOrg', label });
    });

    const sorted = list.sort((a, b) => a.label.localeCompare(b.label));
    if (!q) return sorted.slice(0, 20);
    return sorted.filter((item) => item.label.toLowerCase().includes(q)).slice(0, 20);
  }, [clients, contacts, leads, orgSearch]);

  const orgDisplayLabel = useMemo(() => {
    if (!formData.clientId) return '';
    const c = (clients as { id: string; name?: string; organization?: string }[]).find((x) => x.id === formData.clientId);
    return c?.name || c?.organization || 'Organization';
  }, [formData.clientId, clients]);

  const getDealOrganizationLabel = (deal: Deal): string => {
    if (!deal) return '—';
    if (deal.clientId) {
      const client = clientMap.get(deal.clientId) as { name?: string; organization?: string } | undefined;
      const label = String(client?.name || client?.organization || '').trim();
      if (label) return label;
    }
    if (deal.contactId) {
      const contact = contactMap.get(deal.contactId) as { organization?: string } | undefined;
      const label = String(contact?.organization || '').trim();
      if (label) return label;
    }
    if (deal.leadId) {
      const lead = leadMap.get(deal.leadId) as { organization?: string; name?: string } | undefined;
      const label = String(lead?.organization || lead?.name || '').trim();
      if (label) return label;
    }
    const parsed = parseDealNameParts(deal.name);
    if (parsed.organization) return parsed.organization;
    return '—';
  };

  const getDealContactLabel = (deal: Deal): string => {
    if (!deal) return '—';
    if (deal.contactId) {
      const contact = contactMap.get(deal.contactId) as { firstName?: string; lastName?: string } | undefined;
      const name = `${String(contact?.firstName || '').trim()} ${String(contact?.lastName || '').trim()}`.trim();
      return name || 'Contact';
    }
    if (deal.leadId) {
      const lead = leadMap.get(deal.leadId) as { name?: string } | undefined;
      const mappedLeadName = String(lead?.name || '').trim();
      if (mappedLeadName) return mappedLeadName;
      const parsed = parseDealNameParts(deal.name);
      if (parsed.primary) return parsed.primary;
      return 'Lead';
    }
    const parsed = parseDealNameParts(deal.name);
    if (parsed.primary) return parsed.primary;
    return '—';
  };

  const getDealContactEmail = (deal: Deal): string => {
    if (!deal) return '';
    if (deal.contactId) {
      const contact = contactMap.get(deal.contactId) as { email?: string } | undefined;
      return String(contact?.email || '').trim();
    }
    if (deal.leadId) {
      const lead = leadMap.get(deal.leadId) as { email?: string } | undefined;
      return String(lead?.email || '').trim();
    }
    return '';
  };

  const syncDealSidebarForm = (deal: Deal): void => {
    setDealSidebarForm({
      name: String(deal.name || ''),
      value: String(deal.value || ''),
      currency: String(deal.currency || 'US Dollar (USD)'),
      stage: String(deal.stage || dealStages[0]?.id || 'prospecting'),
      probability: Number.isFinite(Number(deal.probability)) ? Number(deal.probability) : 0,
      expectedCloseDate: toDateInputValue(deal.expectedCloseDate),
      clientId: String(deal.clientId || ''),
      notes: String(deal.notes || ''),
    });
  };

  const dealsByStage = useMemo(() => {
    const grouped: Record<string, Deal[]> = {};
    dealStages.forEach((s) => {
      grouped[s.id] = [];
    });

    let filtered = [...deals];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((deal: Deal) => {
        const name = (deal.name || '').toLowerCase();
        const notes = (deal.notes || '').toLowerCase();
        return name.includes(query) || notes.includes(query);
      });
    }

    filtered.forEach((deal: Deal) => {
      const stage = deal.stage || dealStages[0]?.id || 'prospecting';
      if (grouped[stage]) {
        grouped[stage].push(deal);
      } else {
        grouped[dealStages[0]?.id || 'prospecting'] = grouped[dealStages[0]?.id || 'prospecting'] || [];
        grouped[dealStages[0]?.id || 'prospecting'].push(deal);
      }
    });

    return grouped;
  }, [deals, searchQuery, dealStages]);

  useEffect(() => {
    if (!selectedDeal) return;
    const latest = (deals as Deal[]).find((item) => item.id === selectedDeal.id);
    if (!latest) {
      setSelectedDeal(null);
      setShowDealSidebar(false);
      return;
    }
    setSelectedDeal(latest);
  }, [deals, selectedDeal?.id]);

  const selectedDealOrganizationLabel = useMemo(
    () => (selectedDeal ? getDealOrganizationLabel(selectedDeal) : '—'),
    [selectedDeal, clientMap, contactMap, leadMap]
  );
  const selectedDealHeaderTitle = useMemo(() => {
    if (!selectedDeal) return 'Untitled deal';
    const parsed = parseDealNameParts(selectedDeal.name);
    const subtitle = String(selectedDealOrganizationLabel || '').trim();
    if (
      parsed.primary
      && parsed.organization
      && subtitle
      && subtitle !== '—'
      && normalizeText(parsed.organization) === normalizeText(subtitle)
    ) {
      return parsed.primary;
    }
    return String(selectedDeal.name || '').trim() || 'Untitled deal';
  }, [selectedDeal, selectedDealOrganizationLabel]);
  const selectedDealContactLabel = useMemo(
    () => (selectedDeal ? getDealContactLabel(selectedDeal) : '—'),
    [selectedDeal, contactMap, leadMap]
  );
  const selectedDealEmailAddress = useMemo(
    () => String(selectedDeal ? getDealContactEmail(selectedDeal) : '').trim().toLowerCase(),
    [selectedDeal, contactMap, leadMap]
  );
  const dealSidebarStageOptions = useMemo(() => {
    if (!selectedDeal?.stage) return dealStages;
    const hasStage = dealStages.some((stage) => stage.id === selectedDeal.stage);
    if (hasStage) return dealStages;
    return [
      ...dealStages,
      {
        id: selectedDeal.stage,
        name: selectedDeal.stage
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
        probability: Number.isFinite(Number(selectedDeal.probability)) ? Number(selectedDeal.probability) : 0,
      },
    ];
  }, [dealStages, selectedDeal?.stage, selectedDeal?.probability]);
  const shouldLoadDealEmailThreads =
    showDealSidebar
    && dealSidebarTab === 'email'
    && selectedDealEmailAddress.length > 0;

  const {
    data: dealInboxEmailThreadsData,
    isLoading: isLoadingDealInboxEmailThreads,
    isFetching: isRefreshingDealInboxEmailThreads,
    error: dealInboxEmailThreadsError,
    refetch: refetchDealInboxEmailThreads,
  } = useGetEmailsQuery(
    {
      emailAccount: 'all',
      folder: 'inbox',
      limit: 200,
    },
    {
      skip: !shouldLoadDealEmailThreads,
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: dealSentEmailThreadsData,
    isLoading: isLoadingDealSentEmailThreads,
    isFetching: isRefreshingDealSentEmailThreads,
    error: dealSentEmailThreadsError,
    refetch: refetchDealSentEmailThreads,
  } = useGetEmailsQuery(
    {
      emailAccount: 'all',
      folder: 'sent',
      limit: 200,
    },
    {
      skip: !shouldLoadDealEmailThreads,
      refetchOnMountOrArgChange: true,
    }
  );

  const dealEmailThreadsData = useMemo(
    () => mergeEmailResponses(dealInboxEmailThreadsData, dealSentEmailThreadsData),
    [dealInboxEmailThreadsData, dealSentEmailThreadsData]
  );
  const dealEmailThreads = useMemo(
    () => buildEmailThreadsForEntity(dealEmailThreadsData, selectedDealEmailAddress),
    [dealEmailThreadsData, selectedDealEmailAddress]
  );
  const isLoadingDealEmailThreads = isLoadingDealInboxEmailThreads || isLoadingDealSentEmailThreads;
  const isRefreshingDealEmailThreads = isRefreshingDealInboxEmailThreads || isRefreshingDealSentEmailThreads;
  const dealEmailThreadsError =
    dealInboxEmailThreadsError && dealSentEmailThreadsError ? dealInboxEmailThreadsError : null;
  const refetchDealEmailThreads = (): void => {
    refetchDealInboxEmailThreads();
    refetchDealSentEmailThreads();
  };

  const selectedDealClient = useMemo(() => {
    if (!selectedDeal?.clientId) return null;
    return (clients as any[]).find((item) => item.id === selectedDeal.clientId) || null;
  }, [clients, selectedDeal?.clientId]);
  const selectedDealContact = useMemo(() => {
    if (!selectedDeal?.contactId) return null;
    return (contacts as any[]).find((item) => item.id === selectedDeal.contactId) || null;
  }, [contacts, selectedDeal?.contactId]);
  const selectedDealLead = useMemo(() => {
    if (!selectedDeal?.leadId) return null;
    return (leads as any[]).find((item) => item.id === selectedDeal.leadId) || null;
  }, [leads, selectedDeal?.leadId]);
  const selectedDealVisibilityValue = useMemo<'' | 'global' | 'personal'>(() => {
    const rawValue = String((selectedDeal as any)?.visibility || '').trim().toLowerCase();
    if (rawValue === 'personal') return 'personal';
    if (rawValue === 'global') return 'global';
    return '';
  }, [selectedDeal]);
  const selectedDealOwnerDisplayLabel = useMemo(
    () => {
      if (selectedDealVisibilityValue === 'global') return DEFAULT_OWNER_FILTER_LABEL;
      if (selectedDealVisibilityValue === 'personal') return selfOwnerBaseLabel;
      return '';
    },
    [selectedDealVisibilityValue, selfOwnerBaseLabel]
  );
  const selectedDealVisibilityDisplayLabel = useMemo(
    () => (
      selectedDealVisibilityValue === 'personal'
        ? 'Personal (private)'
        : selectedDealVisibilityValue === 'global'
          ? 'Global (visible for everyone)'
          : ''
    ),
    [selectedDealVisibilityValue]
  );
  const selectedDealLabels = useMemo(() => {
    if (!selectedDeal || !Array.isArray(selectedDeal.tags)) return [] as string[];
    return selectedDeal.tags
      .map((label) => String(label || '').trim())
      .filter((label) => label.length > 0);
  }, [selectedDeal]);
  const allAvailableLabels = useMemo(() => {
    const fromDeals: string[] = [];
    (deals as Deal[]).forEach((deal) => {
      if (!Array.isArray(deal.tags)) return;
      deal.tags.forEach((tag) => {
        const normalized = String(tag || '').trim();
        if (normalized) fromDeals.push(normalized);
      });
    });
    const candidates: string[] = [
      ...Object.keys(getSharedLabelColors()),
      ...fromDeals,
      ...customLabels.map((label) => String(label || '').trim()).filter(Boolean),
    ];
    return mergeLabelCatalogCandidates(candidates);
  }, [deals, customLabels]);
  const getLabelVariant = (label: string): string => {
    const currentColors = getSharedLabelColors();
    return currentColors[label] || currentColors[label.toUpperCase()] || 'secondary';
  };
  const normalizeDealSidebarValue = (value: string): string => {
    const normalized = String(value || '').trim();
    return normalized === '—' ? '' : normalized;
  };
const normalizeDealValueForSidebar = (value: string): string => {
  const normalized = normalizeDealSidebarValue(value);
  return /^0+(?:\.0+)?$/.test(normalized) ? '' : normalized;
};
  const getDealPersonTarget = (): DealSidebarTarget | null => {
    if (selectedDeal?.contactId && selectedDealContact) {
      return { type: 'contact', id: selectedDeal.contactId, record: selectedDealContact };
    }
    if (selectedDeal?.leadId && selectedDealLead) {
      return { type: 'lead', id: selectedDeal.leadId, record: selectedDealLead };
    }
    return null;
  };
  const getDealOrganizationTarget = (): DealSidebarTarget | null => {
    if (selectedDeal?.clientId && selectedDealClient) {
      return { type: 'client', id: selectedDeal.clientId, record: selectedDealClient };
    }
    if (selectedDeal?.leadId && selectedDealLead) {
      return { type: 'lead', id: selectedDeal.leadId, record: selectedDealLead };
    }
    if (selectedDeal?.contactId && selectedDealContact) {
      return { type: 'contact', id: selectedDeal.contactId, record: selectedDealContact };
    }
    return null;
  };
  const getDealDetailsTarget = (): DealSidebarTarget | null => {
    if (selectedDeal?.clientId && selectedDealClient) {
      return { type: 'client', id: selectedDeal.clientId, record: selectedDealClient };
    }
    if (selectedDeal?.leadId && selectedDealLead) {
      return { type: 'lead', id: selectedDeal.leadId, record: selectedDealLead };
    }
    if (selectedDeal?.contactId && selectedDealContact) {
      return { type: 'contact', id: selectedDeal.contactId, record: selectedDealContact };
    }
    return selectedDeal ? { type: 'deal', id: selectedDeal.id, record: selectedDeal } : null;
  };
  const getDealFieldValue = (fieldId: DealEditableFieldId): string => {
    const personTarget = getDealPersonTarget();
    const organizationTarget = getDealOrganizationTarget();
    const detailsTarget = getDealDetailsTarget();
    const personRecord: any = personTarget?.record || {};
    const organizationRecord: any = organizationTarget?.record || {};
    const detailsRecord: any = detailsTarget?.record || {};
    const selectedDealRecord: any = selectedDeal || {};

    switch (fieldId) {
      case 'owner':
        return selectedDealOwnerDisplayLabel;
      case 'visibility':
        return selectedDealVisibilityDisplayLabel;
      case 'stage':
        return formatStageName(String(selectedDealRecord.stage || ''));
      case 'name':
        if (personTarget?.type === 'contact') {
          return `${String(personRecord.firstName || '').trim()} ${String(personRecord.lastName || '').trim()}`.trim();
        }
        if (personTarget?.type === 'lead') {
          return String(personRecord.name || '').trim();
        }
        return normalizeDealSidebarValue(selectedDealContactLabel);
      case 'email':
        return normalizeDealSidebarValue(String(personRecord.email || '').trim() || selectedDealEmailAddress);
      case 'phone':
        return String(personRecord.phone || '').trim();
      case 'contactStage':
        return String(personRecord.contactStage || '').trim();
      case 'value':
        return String(selectedDealRecord.value || '').trim();
      case 'expectedCloseDate':
        return String(selectedDealRecord.expectedCloseDate || '').trim();
      case 'businessUnit':
        return String(detailsRecord.businessUnit || selectedDealRecord.businessUnit || '').trim()
          || (detailsRecord.inStore || selectedDealRecord.inStore ? 'In-Store' : '');
      case 'sourceOrigin':
        return String(detailsRecord.sourceOrigin || selectedDealRecord.sourceOrigin || '').trim();
      case 'sourceChannel':
        return String(detailsRecord.sourceChannel || selectedDealRecord.sourceChannel || '').trim();
      case 'sourceChannelId':
        return String(detailsRecord.sourceChannelId || selectedDealRecord.sourceChannelId || '').trim();
      case 'organization':
        if (organizationTarget?.type === 'client') {
          return String(organizationRecord.organization || organizationRecord.name || '').trim();
        }
        if (organizationTarget?.type === 'lead') {
          return String(organizationRecord.organization || organizationRecord.name || '').trim();
        }
        if (organizationTarget?.type === 'contact') {
          return String(organizationRecord.organization || '').trim();
        }
        return normalizeDealSidebarValue(selectedDealOrganizationLabel);
      case 'accountStage':
        return String(organizationRecord.accountStage || '').trim();
      case 'address':
        return String(organizationRecord.address || '').trim();
      case 'website':
        return String(organizationRecord.website || '').trim();
      case 'linkedInProfile':
        return String(organizationRecord.linkedInProfile || '').trim();
      case 'numberOfEmployees':
        return String(organizationRecord.numberOfEmployees || '').trim();
      case 'annualRevenue':
        return String(organizationRecord.annualRevenue || '').trim();
      case 'industry':
        return String(organizationRecord.industry || '').trim();
      default:
        return '';
    }
  };
  const startEditingDealSidebarField = (fieldId: DealEditableFieldId) => {
    const currentValue = fieldId === 'value'
      ? normalizeDealValueForSidebar(getDealFieldValue(fieldId))
      : getDealFieldValue(fieldId);
    if (fieldId === 'owner') {
      setDealSidebarFieldDraftValue(selectedDealVisibilityValue === 'personal' ? selfOwnerBaseLabel : DEFAULT_OWNER_FILTER_LABEL);
    } else if (fieldId === 'visibility') {
      setDealSidebarFieldDraftValue(selectedDealVisibilityValue || 'global');
    } else if (fieldId === 'expectedCloseDate') {
      setDealSidebarFieldDraftValue(toDateInputValue(currentValue));
    } else if (fieldId === 'stage') {
      setDealSidebarFieldDraftValue(String(selectedDeal?.stage || dealStages[0]?.id || 'prospecting'));
    } else {
      setDealSidebarFieldDraftValue(currentValue);
    }
    setDealSidebarEditingFieldId(fieldId);
  };
  const cancelEditingDealSidebarField = () => {
    setDealSidebarEditingFieldId(null);
    setDealSidebarFieldDraftValue('');
  };
  const saveDealEntityUpdate = async (
    target: DealSidebarTarget,
    payload: Record<string, unknown>
  ) => {
    if (target.type === 'deal') {
      await updateDeal({ dealId: target.id, ...payload }).unwrap();
      await refetchDeals();
      setSelectedDeal((prev) => (prev && prev.id === target.id ? ({ ...prev, ...payload } as Deal) : prev));
      setDealSidebarForm((prev) => ({
        ...prev,
        ...(payload.name !== undefined ? { name: String(payload.name || '') } : {}),
        ...(payload.value !== undefined ? { value: String(payload.value || '') } : {}),
        ...(payload.currency !== undefined ? { currency: String(payload.currency || 'US Dollar (USD)') } : {}),
        ...(payload.stage !== undefined ? { stage: String(payload.stage || '') } : {}),
        ...(payload.expectedCloseDate !== undefined ? { expectedCloseDate: toDateInputValue(String(payload.expectedCloseDate || '')) } : {}),
        ...(payload.clientId !== undefined ? { clientId: String(payload.clientId || '') } : {}),
      }));
      return;
    }

    if (target.type === 'lead') {
      await updateLead({ leadId: target.id, ...payload } as any).unwrap();
      await refetchLeads();
      return;
    }

    if (target.type === 'contact') {
      await updateContact({ contactId: target.id, ...payload } as any).unwrap();
      await refetchContacts();
      return;
    }

    await updateClient({ clientId: target.id, ...payload } as any).unwrap();
    await refetchClients();
  };
  const handleSaveDealSidebarField = async (fieldId: DealEditableFieldId) => {
    if (!selectedDeal) return;

    if (fieldId === 'owner' || fieldId === 'visibility') {
      const currentVisibility = selectedDealVisibilityValue;
      const nextVisibility = fieldId === 'owner'
        ? (String(dealSidebarFieldDraftValue || '').trim().toLowerCase() === DEFAULT_OWNER_FILTER_LABEL.toLowerCase() ? 'global' : 'personal')
        : (String(dealSidebarFieldDraftValue || '').trim().toLowerCase() === 'personal' ? 'personal' : 'global');
      if (currentVisibility === nextVisibility) {
        cancelEditingDealSidebarField();
        return;
      }

      try {
        await saveDealEntityUpdate({ type: 'deal', id: selectedDeal.id }, { visibility: nextVisibility });
        cancelEditingDealSidebarField();
      } catch (error: any) {
        console.error(`Error updating ${fieldId}:`, error);
        alert(`Failed to update ${fieldId === 'owner' ? 'Owner' : 'Visibility'}: ${error?.data?.message || error?.message || 'Unknown error'}`);
      }
      return;
    }

    const personTarget = getDealPersonTarget();
    const organizationTarget = getDealOrganizationTarget();
    const detailsTarget = getDealDetailsTarget();

    const draftValue = DEAL_EDITABLE_FIELD_CONFIG[fieldId].inputType === 'date'
      ? String(dealSidebarFieldDraftValue || '').trim()
      : String(dealSidebarFieldDraftValue || '').trim();
    const currentValue = normalizeDealSidebarValue(getDealFieldValue(fieldId));
    if (String(currentValue || '') === String(draftValue || '')) {
      cancelEditingDealSidebarField();
      return;
    }

    try {
      if (fieldId === 'stage') {
        await saveDealEntityUpdate({ type: 'deal', id: selectedDeal.id }, { stage: draftValue || dealStages[0]?.id || 'prospecting' });
        cancelEditingDealSidebarField();
        return;
      }

      if (fieldId === 'value') {
        await saveDealEntityUpdate({ type: 'deal', id: selectedDeal.id }, { value: draftValue });
        cancelEditingDealSidebarField();
        return;
      }

      if (fieldId === 'expectedCloseDate') {
        await saveDealEntityUpdate({ type: 'deal', id: selectedDeal.id }, { expectedCloseDate: draftValue || undefined });
        cancelEditingDealSidebarField();
        return;
      }

      if (fieldId === 'name') {
        if (personTarget?.type === 'contact') {
          const [firstName = '', ...rest] = String(draftValue || '').split(/\s+/).filter(Boolean);
          await saveDealEntityUpdate(personTarget, {
            firstName,
            lastName: rest.join(' '),
          });
        } else if (personTarget?.type === 'lead') {
          await saveDealEntityUpdate(personTarget, { name: draftValue });
        } else {
          await saveDealEntityUpdate({ type: 'deal', id: selectedDeal.id }, { name: draftValue || 'Untitled deal' });
        }
        cancelEditingDealSidebarField();
        return;
      }

      if (fieldId === 'email') {
        if (personTarget) {
          await saveDealEntityUpdate(personTarget, { email: draftValue || undefined });
        } else {
          await saveDealEntityUpdate({ type: 'deal', id: selectedDeal.id }, { email: draftValue || undefined });
        }
        cancelEditingDealSidebarField();
        return;
      }

      if (fieldId === 'organization') {
        if (organizationTarget) {
          const organizationRecord: any = organizationTarget.record || {};
          const payload = organizationTarget.type === 'client'
            ? { organization: draftValue, ...(organizationRecord.name ? { name: draftValue } : {}) }
            : { organization: draftValue };
          await saveDealEntityUpdate(organizationTarget, payload);
        } else {
          await saveDealEntityUpdate({ type: 'deal', id: selectedDeal.id }, { organization: draftValue || undefined });
        }
        cancelEditingDealSidebarField();
        return;
      }

      if (fieldId === 'businessUnit') {
        const target = detailsTarget || organizationTarget || personTarget || { type: 'deal' as const, id: selectedDeal.id };
        const nextBusinessUnit = String(draftValue || '').trim();
        const nextInStore = nextBusinessUnit.toLowerCase() === 'in-store' || nextBusinessUnit.toLowerCase() === 'in store';
        await saveDealEntityUpdate(target, {
          businessUnit: nextBusinessUnit || undefined,
          inStore: nextInStore,
        });
        cancelEditingDealSidebarField();
        return;
      }

      const fieldTarget: DealSidebarTarget =
        fieldId === 'contactStage' || fieldId === 'phone'
          ? (personTarget || { type: 'deal', id: selectedDeal.id })
          : fieldId === 'accountStage'
            || fieldId === 'address'
            || fieldId === 'website'
            || fieldId === 'linkedInProfile'
            || fieldId === 'numberOfEmployees'
            || fieldId === 'annualRevenue'
            || fieldId === 'industry'
              ? (organizationTarget || detailsTarget || { type: 'deal', id: selectedDeal.id })
              : (detailsTarget || organizationTarget || personTarget || { type: 'deal', id: selectedDeal.id });

      await saveDealEntityUpdate(fieldTarget, { [fieldId]: draftValue || undefined });
      cancelEditingDealSidebarField();
    } catch (error: any) {
      console.error(`Error updating ${fieldId}:`, error);
      alert(`Failed to update ${DEAL_EDITABLE_FIELD_CONFIG[fieldId].placeholder}: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };
  const toggleDealLabel = async (label: string) => {
    if (!selectedDeal) return;
    const currentLabels = Array.isArray(selectedDeal.tags) ? selectedDeal.tags : [];
    const isAssigned = currentLabels.includes(label);
    const updatedLabels = isAssigned
      ? currentLabels.filter((item) => item !== label)
      : [...currentLabels, label];

    try {
      await updateDeal({
        dealId: selectedDeal.id,
        tags: updatedLabels
      }).unwrap();
      setSelectedDeal((prev) => (prev ? { ...prev, tags: updatedLabels } : prev));
      await refetchDeals();
    } catch (error: any) {
      console.error('Error updating deal labels:', error);
      alert(`Failed to update labels: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };
  const handleCreateNewDealLabel = async () => {
    if (!selectedDeal) return;
    const rawValue = window.prompt('Label name');
    if (!rawValue) return;
    const nextLabel = rawValue.trim();
    if (!nextLabel) return;

    const existingLabel = allAvailableLabels.find(
      (label) => label.toLowerCase() === nextLabel.toLowerCase()
    );
    const finalLabel = existingLabel || nextLabel;
    if (!existingLabel) {
      const updatedCustomLabels = [...customLabels, finalLabel];
      setCustomLabels(updatedCustomLabels);
      localStorage.setItem('customLabels', JSON.stringify(updatedCustomLabels));
    }

    await toggleDealLabel(finalLabel);
  };
  const renderEditableDealLabelsRow = (
    fieldId: LabelsFieldId,
    showAll: boolean
  ) => {
    if (!selectedDeal) return null;

    const isOpen = showLabelsDropdownField === fieldId;
    const isHovered = dealSidebarHoveredField === fieldId;
    const hasLabels = selectedDealLabels.length > 0;
    const showAddPrompt = isHovered && !hasLabels;
    const shouldRender = showAll || hasLabels || isOpen;
    if (!shouldRender) return null;

    const filteredLabelOptions = allAvailableLabels.filter((label) =>
      label.toLowerCase().includes(labelSearchQuery.toLowerCase())
    );

    return (
      <Dropdown
        key={fieldId}
        show={isOpen}
        onToggle={(nextIsOpen) => {
          if (nextIsOpen) {
            setShowLabelsDropdownField(fieldId);
          } else {
            setShowLabelsDropdownField(null);
            setLabelSearchQuery('');
          }
        }}
        className="mb-2"
        style={{ marginBottom: 0 }}
      >
        <Dropdown.Toggle
          as="div"
          className="text-decoration-none labels-dropdown-toggle"
          style={{
            width: '100%',
            padding: '0.5rem',
            borderRadius: '4px',
            backgroundColor: isHovered ? '#f8f9fa' : 'transparent',
            transition: 'background-color 0.2s',
            cursor: 'pointer',
            minHeight: '32px',
            border: 'none',
            textAlign: 'left'
          }}
          onMouseEnter={() => setDealSidebarHoveredField(fieldId)}
          onMouseLeave={() => setDealSidebarHoveredField(null)}
        >
          <div className="d-flex align-items-center gap-2">
            <span style={dealSidebarIconStyle}>
              <Tag size={14} />
            </span>
            {hasLabels ? (
              <div className="d-flex flex-wrap gap-1 align-items-center">
                {selectedDealLabels.map((label) => (
                  <Badge key={`${fieldId}-${label}`} bg={getLabelVariant(label)} style={{ fontSize: '0.55rem', padding: '0.15rem 0.35rem' }}>
                    {label}
                  </Badge>
                ))}
              </div>
            ) : (
              <span
                style={{
                  fontSize: '0.75rem',
                  color: showAddPrompt ? '#0d6efd' : '#6c757d',
                  fontWeight: showAddPrompt ? 500 : 'normal'
                }}
              >
                {showAddPrompt ? '+ Add Labels' : 'Labels'}
              </span>
            )}
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu
          show={isOpen}
          style={{
            minWidth: '250px',
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '0.5rem',
            marginTop: '0.25rem'
          }}
        >
          <div className="mb-2">
            <InputGroup size="sm">
              <InputGroup.Text>
                <Search size={14} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search label"
                value={labelSearchQuery}
                onChange={(event) => setLabelSearchQuery(event.target.value)}
                onClick={(event) => event.stopPropagation()}
                style={{ fontSize: '0.75rem' }}
              />
            </InputGroup>
          </div>
          {filteredLabelOptions.map((label) => {
            const isAssigned = selectedDealLabels.includes(label);
            return (
              <Dropdown.Item
                key={`${fieldId}-${label}-option`}
                active={isAssigned}
                onClick={async (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  await toggleDealLabel(label);
                }}
                style={{ fontSize: '0.75rem' }}
              >
                <div className="d-flex align-items-center">
                  {isAssigned && <Check className="me-2" size={14} style={{ color: '#0d6efd' }} />}
                  <Badge bg={getLabelVariant(label)} style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem' }}>
                    {label}
                  </Badge>
                </div>
              </Dropdown.Item>
            );
          })}
          <Dropdown.Divider />
          <Dropdown.Item
            onClick={async (event) => {
              event.preventDefault();
              event.stopPropagation();
              await handleCreateNewDealLabel();
            }}
            style={{ fontSize: '0.75rem', color: '#0d6efd' }}
          >
            <div className="d-flex align-items-center">
              <Plus className="me-2" size={14} />
              <span>Add new label</span>
            </div>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  };
  const renderDealSidebarField = (
    fieldId: DealEditableFieldId,
    label: string,
    Icon: React.ComponentType<{ size?: number; className?: string }>,
    options: { showAll: boolean; showInfo?: boolean } = { showAll: false }
  ) => {
    if (!selectedDeal) return null;

    const config = DEAL_EDITABLE_FIELD_CONFIG[fieldId];
    const displayValue = fieldId === 'value'
      ? normalizeDealValueForSidebar(getDealFieldValue(fieldId))
      : normalizeDealSidebarValue(getDealFieldValue(fieldId));
    const isEditing = dealSidebarEditingFieldId === fieldId;
    const isHovered = dealSidebarHoveredField === fieldId;
    const shouldRender = options.showAll || Boolean(displayValue) || isEditing;
    if (!shouldRender) return null;

    const showAddPrompt = isHovered && !displayValue && !isEditing;
    const inputType = config.inputType === 'date'
      ? 'date'
      : fieldId.toLowerCase().includes('email')
        ? 'email'
        : fieldId.toLowerCase().includes('phone')
          ? 'tel'
          : 'text';
    const detailsTarget = getDealDetailsTarget();
    const stageOptions = fieldId === 'accountStage'
      ? accountStageOptions
      : fieldId === 'contactStage'
        ? contactStageOptions
        : [];
    const businessUnitOptions = Array.from(new Set(
      ['In-Store', String((detailsTarget?.record as any)?.businessUnit || '').trim()].filter(Boolean)
    ));
    const displayText = fieldId === 'value' && displayValue
      ? formatCurrency(displayValue, String(selectedDeal.currency || dealSidebarForm.currency || 'US Dollar (USD)'))
      : fieldId === 'expectedCloseDate' && displayValue
        ? toLocaleDateStringInTimezone(
            displayValue,
            'en-US',
            { month: 'short', day: 'numeric', year: 'numeric' },
            tenantTimezone
          )
        : displayValue;
    const isUpdatingAnySidebarTarget = isUpdatingDeal || isUpdatingLead || isUpdatingClient || isUpdatingContact;

    return (
      <div
        className="mb-2"
        style={{
          cursor: 'pointer',
          padding: '0.5rem',
          borderRadius: '4px',
          backgroundColor: isHovered && !isEditing ? '#f8f9fa' : 'transparent',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={() => setDealSidebarHoveredField(fieldId)}
        onMouseLeave={() => setDealSidebarHoveredField(null)}
        onClick={() => startEditingDealSidebarField(fieldId)}
      >
        <div className="d-flex align-items-center gap-2">
          <span style={dealSidebarIconStyle}>
            <Icon size={14} />
          </span>
          <span
            style={{
              fontSize: '0.75rem',
              color: showAddPrompt ? '#0d6efd' : (displayText ? '#212529' : '#6c757d'),
              fontWeight: showAddPrompt ? 500 : 'normal'
            }}
          >
            {displayText || (showAddPrompt ? '+ Add value' : label)}
          </span>
          {options.showInfo && <InfoCircle size={9} className="ms-1 text-muted" />}
        </div>
        {isEditing && (
          <div className="d-flex align-items-center gap-2 mt-2" onClick={(event) => event.stopPropagation()}>
            {fieldId === 'owner' ? (
              <Form.Select
                value={dealSidebarFieldDraftValue || (selectedDealVisibilityValue === 'personal' ? selfOwnerBaseLabel : DEFAULT_OWNER_FILTER_LABEL)}
                onChange={(event) => setDealSidebarFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                <option value={DEFAULT_OWNER_FILTER_LABEL}>{DEFAULT_OWNER_FILTER_LABEL}</option>
                <option value={selfOwnerBaseLabel}>{selfOwnerBaseLabel}</option>
              </Form.Select>
            ) : fieldId === 'visibility' ? (
              <Form.Select
                value={dealSidebarFieldDraftValue || selectedDealVisibilityValue || 'global'}
                onChange={(event) => setDealSidebarFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                <option value="global">Global (visible for everyone)</option>
                <option value="personal">Personal (private)</option>
              </Form.Select>
            ) : fieldId === 'stage' ? (
              <Form.Select
                value={dealSidebarFieldDraftValue || selectedDeal.stage || dealStages[0]?.id || 'prospecting'}
                onChange={(event) => setDealSidebarFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                {dealSidebarStageOptions.map((option) => (
                  <option key={`deal-stage-option-${option.id}`} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>
            ) : fieldId === 'accountStage' || fieldId === 'contactStage' ? (
              <Form.Select
                value={dealSidebarFieldDraftValue}
                onChange={(event) => setDealSidebarFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                <option value="">Select</option>
                {stageOptions.map((option) => (
                  <option key={`${fieldId}-option-${option}`} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            ) : fieldId === 'businessUnit' ? (
              <Form.Select
                value={dealSidebarFieldDraftValue}
                onChange={(event) => setDealSidebarFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                <option value="">Select business unit</option>
                {businessUnitOptions.map((option) => (
                  <option key={`deal-business-unit-${option}`} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <Form.Control
                type={inputType}
                value={dealSidebarFieldDraftValue}
                placeholder={label}
                onChange={(event) => setDealSidebarFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              />
            )}
            {fieldId !== 'owner' && fieldId !== 'visibility' && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  setDealSidebarFieldDraftValue('');
                }}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
              >
                Clear
              </Button>
            )}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                cancelEditingDealSidebarField();
              }}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                handleSaveDealSidebarField(fieldId);
              }}
              disabled={isUpdatingAnySidebarTarget}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
            >
              {isUpdatingAnySidebarTarget ? <Spinner size="sm" animation="border" /> : 'Save'}
            </Button>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!showDealSidebar) return;
    setShowAllDealDetailFields(false);
    setShowAllDealPersonFields(false);
    setShowAllDealOrganizationFields(false);
    setDealSidebarHoveredField(null);
    setDealSidebarEditingFieldId(null);
    setDealSidebarFieldDraftValue('');
    setShowLabelsDropdownField(null);
    setLabelSearchQuery('');
  }, [showDealSidebar, selectedDeal?.id]);

  const ensureWonDealLinkedToClient = async (deal: Deal): Promise<void> => {
    if (!deal?.id || deal.clientId) return;

    const contact = (contacts as { id: string; firstName?: string; lastName?: string; organization?: string; email?: string }[])
      .find((item) => item.id === deal.contactId);
    const lead = (leads as { id: string; name?: string; organization?: string; email?: string }[])
      .find((item) => item.id === deal.leadId);

    const organizationName = String(contact?.organization || lead?.organization || '').trim();
    const contactName = contact
      ? `${String(contact.firstName || '').trim()} ${String(contact.lastName || '').trim()}`.trim()
      : String(lead?.name || '').trim();
    const clientName = organizationName || contactName || String(deal.name || '').trim();

    if (!clientName) return;

    const existingClient = (clients as { id: string; name?: string; organization?: string }[]).find((client) => {
      return normalizeText(client.name || client.organization) === normalizeText(clientName);
    });

    let clientId = existingClient?.id;
    if (!clientId) {
      const createdClientResponse: any = await createClient({
        name: clientName,
        organization: organizationName || clientName,
        email: contact?.email || lead?.email || undefined,
        status: 'current',
        visibility: 'personal',
      }).unwrap();
      clientId = createdClientResponse?.data?.client?.id || createdClientResponse?.client?.id;
    }

    if (clientId) {
      await updateDeal({ dealId: deal.id, clientId }).unwrap();
    }
  };

  const handleSaveDeal = async () => {
    try {
      let notes = formData.notes || '';
      if ((formData.contactName || formData.personPhone || formData.personEmail) && !formData.contactId && !formData.leadId) {
        const parts: string[] = [];
        if (formData.contactName) parts.push(`Contact: ${formData.contactName}`);
        if (formData.personPhone) parts.push(`Phone: ${formData.personPhone}`);
        if (formData.personEmail) parts.push(`Email: ${formData.personEmail}`);
        if (parts.length) notes = [notes, parts.join('; ')].filter(Boolean).join('\n');
      }
      const dealData = {
        name: formData.name || 'Untitled deal',
        value: String(formData.value || '').trim(),
        currency: formData.currency,
        stage: formData.stage || addDealStages[0]?.id || 'prospecting',
        probability: parseInt(formData.probability.toString()) || 0,
        expectedCloseDate: formData.expectedCloseDate || undefined,
        clientId: formData.clientId || undefined,
        contactId: formData.contactId || undefined,
        leadId: formData.leadId || undefined,
        notes: notes || undefined,
        tags: formData.tags ? [formData.tags] : [],
        visibility: 'personal'
      };

      await createDeal(dealData).unwrap();
      
      setFormData({
        name: '',
        value: '',
        currency: 'US Dollar (USD)',
        stage: addDealStages[0]?.id || 'prospecting',
        probability: 0,
        expectedCloseDate: '',
        clientId: '',
        contactId: '',
        leadId: '',
        contactName: '',
        personPhone: '',
        personEmail: '',
        notes: '',
        tags: ''
      });

      setShowAddDealModal(false);
      refetchDeals();
    } catch (error: any) {
      console.error('Error creating deal:', error);
      alert(`Failed to create deal: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleFormChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleOpenDealSidebar = (deal: Deal): void => {
    setSelectedDeal(deal);
    syncDealSidebarForm(deal);
    setDealSidebarTab('notes');
    setShowDealSidebar(true);
  };

  const handleCloseDealSidebar = (): void => {
    setShowDealSidebar(false);
    setSelectedDeal(null);
  };

  const handleDealSidebarFieldChange = (field: keyof typeof dealSidebarForm, value: string | number): void => {
    setDealSidebarForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDealFromSidebar = async (): Promise<void> => {
    if (!selectedDeal) return;

    const trimmedName = String(dealSidebarForm.name || '').trim();
    if (!trimmedName) {
      alert('Deal title is required.');
      return;
    }

    setIsSavingSidebarDeal(true);
    try {
      const payload = {
        dealId: selectedDeal.id,
        name: trimmedName,
        value: String(dealSidebarForm.value || '').trim(),
        currency: String(dealSidebarForm.currency || 'US Dollar (USD)'),
        stage: String(dealSidebarForm.stage || selectedDeal.stage || dealStages[0]?.id || 'prospecting'),
        probability: Number.isFinite(Number(dealSidebarForm.probability)) ? Number(dealSidebarForm.probability) : 0,
        expectedCloseDate: String(dealSidebarForm.expectedCloseDate || '').trim() || undefined,
        clientId: String(dealSidebarForm.clientId || '').trim() || undefined,
        notes: String(dealSidebarForm.notes || '').trim() || undefined,
      };
      const dealPatch: Partial<Deal> = {
        name: payload.name,
        value: payload.value,
        currency: payload.currency,
        stage: payload.stage,
        probability: payload.probability,
        expectedCloseDate: payload.expectedCloseDate,
        clientId: payload.clientId,
        notes: payload.notes,
      };

      await updateDeal(payload).unwrap();
      await refetchDeals();
      setSelectedDeal((prev) => (prev ? { ...prev, ...dealPatch } : prev));
    } catch (error: any) {
      console.error('Error saving deal from sidebar:', error);
      alert(`Failed to update deal: ${error?.data?.message || error?.message || 'Unknown error'}`);
    } finally {
      setIsSavingSidebarDeal(false);
    }
  };

  const handleDeleteDealFromSidebar = async (): Promise<void> => {
    if (!selectedDeal) return;
    const shouldDelete = window.confirm(`Delete deal "${selectedDeal.name || 'Untitled deal'}"? This cannot be undone.`);
    if (!shouldDelete) return;

    try {
      await deleteDeal(selectedDeal.id).unwrap();
      await refetchDeals();
      handleCloseDealSidebar();
    } catch (error: any) {
      console.error('Error deleting deal from sidebar:', error);
      alert(`Failed to delete deal: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleDragStart = (deal: Deal) => {
    setDraggedDeal(deal);
    setDragActionHover(null);
  };

  const handleDragEnd = () => {
    setDraggedDeal(null);
    setDragActionHover(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const moveDealToStage = async (deal: Deal, targetStage: string): Promise<void> => {
    if (!deal?.id || !targetStage || deal.stage === targetStage) return;

    await updateDeal({
      dealId: deal.id,
      stage: targetStage
    }).unwrap();

    if (isWonStage(targetStage)) {
      triggerWonConfetti();
      try {
        await ensureWonDealLinkedToClient({ ...deal, stage: targetStage });
      } catch (clientError) {
        console.error('Deal moved to won, but failed to link/create client:', clientError);
        alert('Deal was moved to Won, but client auto-move failed. Please retry or create a client manually.');
      }
    }
  };

  const handleDrop = async (targetStage: string) => {
    if (draggedDeal && draggedDeal.stage !== targetStage) {
      const droppedDeal = draggedDeal;
      try {
        await moveDealToStage(droppedDeal, targetStage);
        refetchDeals();
      } catch (error: any) {
        console.error('Error updating deal stage:', error);
        alert(`Failed to update deal: ${error?.data?.message || error?.message || 'Unknown error'}`);
      }
    }
    setDragActionHover(null);
    setDraggedDeal(null);
  };

  const handleActionDragOver = (event: React.DragEvent, action: DragAction) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActionHover(action);
  };

  const handleActionDrop = async (event: React.DragEvent, action: DragAction) => {
    event.preventDefault();
    event.stopPropagation();

    const activeDeal = draggedDeal;
    if (!activeDeal) return;

    try {
      if (action === 'delete') {
        await deleteDeal(activeDeal.id).unwrap();
      } else if (action === 'lost') {
        if (!lostStageId) {
          alert('No Lost stage is configured in this pipeline.');
        } else {
          await moveDealToStage(activeDeal, lostStageId);
        }
      } else if (action === 'won') {
        if (!wonStageId) {
          alert('No Won stage is configured in this pipeline.');
        } else {
          await moveDealToStage(activeDeal, wonStageId);
        }
      } else if (action === 'move') {
        // Keep drag behavior simple: user can drop directly on any stage column.
        return;
      }

      await refetchDeals();
    } catch (error: any) {
      console.error('Error applying drag action:', error);
      alert(`Failed to apply action: ${error?.data?.message || error?.message || 'Unknown error'}`);
    } finally {
      setDragActionHover(null);
      setDraggedDeal(null);
    }
  };

  const formatCurrency = (value: string, currency: string = 'USD') => {
    const numValue = parseFloat(value) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency === 'US Dollar (USD)' ? 'USD' : currency
    }).format(numValue);
  };

  const getStageColor = (stageId: string) => {
    const colors: Record<string, string> = {
      'prospecting': '#6c757d',
      'qualification': '#0d6efd',
      'proposal': '#ffc107',
      'negotiation': '#fd7e14',
      'closed-won': '#198754',
      'closed-lost': '#dc3545',
    };
    return colors[stageId] || '#6c757d';
  };

  const formatStageName = (stageId: string) => {
    const s = dealStages.find((x) => x.id === stageId);
    return s?.name || stageId.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const handleEditPipeline = () => {
    setIsNewPipeline(false);
    setEditingPipeline(selectedPipeline ? { ...selectedPipeline, stages: selectedPipeline.stages.map((s) => ({ ...s })) } : null);
    setShowEditPipelineModal(true);
    setShowPipelineDropdown(false);
  };

  const handleSaveEditPipeline = async () => {
    if (!editingPipeline) return;
    let next: Pipeline[];
    if (isNewPipeline) {
      next = [...pipelines, { ...editingPipeline, order: pipelines.length }];
      setSelectedPipelineId(editingPipeline.id);
    } else {
      next = pipelines.map((p) => (p.id === editingPipeline.id ? editingPipeline : p));
    }
    await persistPipelines(next);
    setShowEditPipelineModal(false);
    setEditingPipeline(null);
    setIsNewPipeline(false);
  };

  const [isNewPipeline, setIsNewPipeline] = useState(false);

  const handleNewPipeline = () => {
    const id = `pipeline-${Date.now()}`;
    setIsNewPipeline(true);
    setEditingPipeline({
      id,
      name: 'New Pipeline',
      stages: [...DEFAULT_STAGES],
      dealProbability: true,
      order: pipelines.length,
    });
    setShowPipelineDropdown(false);
    setShowEditPipelineModal(true);
  };

  const handleReorderPipelines = () => {
    setReorderPipelines([...pipelines]);
    setShowReorderModal(true);
    setShowPipelineDropdown(false);
  };

  const handleMoveReorderPipeline = (index: number, direction: 'up' | 'down') => {
    const next = [...reorderPipelines];
    const j = direction === 'up' ? index - 1 : index + 1;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setReorderPipelines(next);
  };

  const handleSaveReorderPipelines = async () => {
    await persistPipelines(reorderPipelines.map((p, i) => ({ ...p, order: i })));
    setShowReorderModal(false);
  };

  if (isLoadingDeals) {
    return (
      <Container fluid className="py-4 d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  if (dealsError) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">
          <Alert.Heading>Error loading deals</Alert.Heading>
          <p>{(dealsError as any)?.data?.message || 'Failed to load deals'}</p>
          <Button onClick={() => refetchDeals()}>Retry</Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-0" style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      {wonConfettiPieces.length > 0 && (
        <>
          <style>{`
            @keyframes dealsConfettiFall {
              0% { transform: translate3d(0, -12vh, 0) rotate(0deg); opacity: 1; }
              100% { transform: translate3d(0, 110vh, 0) rotate(720deg); opacity: 0.8; }
            }
          `}</style>
          <div
            style={{
              position: 'fixed',
              inset: 0,
              overflow: 'hidden',
              pointerEvents: 'none',
              zIndex: 1200,
            }}
          >
            {wonConfettiPieces.map((piece) => (
              <span
                key={piece.id}
                style={{
                  position: 'absolute',
                  top: '-24px',
                  left: `${piece.left}%`,
                  width: `${piece.size}px`,
                  height: `${Math.max(4, Math.round(piece.size * 0.45))}px`,
                  borderRadius: '999px',
                  backgroundColor: piece.color,
                  transform: `rotate(${piece.rotate}deg)`,
                  animation: `dealsConfettiFall ${piece.duration}s ease-out ${piece.delay}s forwards`,
                  opacity: 0.95,
                }}
              />
            ))}
          </div>
        </>
      )}
      {/* Page Header - same as Leads/Clients/Contacts */}
      <div style={{ backgroundColor: '#ffffff', paddingTop: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #dee2e6' }}>
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <h5 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: 500, color: '#212529' }}>Deals</h5>
            </div>
            <div className="d-flex align-items-center gap-2">
              <InputGroup size="sm" style={{ width: '200px' }}>
                <InputGroup.Text style={{ backgroundColor: 'white', borderRight: 'none', fontSize: '0.75rem' }}>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search Kross Global"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ borderLeft: 'none', fontSize: '0.75rem' }}
                  size="sm"
                />
              </InputGroup>
            </div>
          </div>
        </Container>
      </div>

      {/* Main Content */}
      <div className="py-3" style={{ fontSize: '0.75rem' }}>
      {/* Action Bar */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <Button 
            variant="primary" 
            size="sm" 
            onClick={() => {
              setFormData((p) => ({ ...p, stage: dealStages[0]?.id || 'prospecting' }));
              setShowAddDealModal(true);
            }}
            style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem' }}
          >
            <Plus size={14} className="me-1" />
            Deal
          </Button>
          <span style={{ fontSize: '0.75rem', marginLeft: '0.5rem' }}>
            {deals.length} deal{deals.length !== 1 ? 's' : ''}
          </span>
          <div className="d-flex align-items-center gap-1 ms-3">
            <OverlayTrigger placement="bottom" overlay={<Tooltip id="view-kanban">Pipeline</Tooltip>}>
              <button
                type="button"
                onClick={() => setViewMode('kanban')}
                style={{
                  background: viewMode === 'kanban' ? '#0d6efd' : '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: 4,
                  padding: '0.25rem 0.4rem',
                  cursor: 'pointer',
                  color: viewMode === 'kanban' ? '#fff' : '#212529',
                }}
              >
                <Kanban size={16} />
              </button>
            </OverlayTrigger>
            <OverlayTrigger placement="bottom" overlay={<Tooltip id="view-list">List</Tooltip>}>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                style={{
                  background: viewMode === 'list' ? '#0d6efd' : '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: 4,
                  padding: '0.25rem 0.4rem',
                  cursor: 'pointer',
                  color: viewMode === 'list' ? '#fff' : '#212529',
                }}
              >
                <List size={16} />
              </button>
            </OverlayTrigger>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Dropdown show={showPipelineDropdown} onToggle={(v) => setShowPipelineDropdown(!!v)} align="end">
            <Dropdown.Toggle
              variant="light"
              size="sm"
              style={{ fontSize: '0.75rem', border: '1px solid #dee2e6', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Kanban size={14} />
              <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedPipeline?.name || 'Select pipeline'}
              </span>
              <ChevronDown size={12} />
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ minWidth: 220, fontSize: '0.8rem' }}>
              {pipelines.map((p) => (
                <Dropdown.Item
                  key={p.id}
                  onClick={() => {
                    setSelectedPipelineId(p.id);
                    setShowPipelineDropdown(false);
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <span>{p.name}</span>
                    {p.id === selectedPipelineId && <Check size={14} style={{ color: '#0d6efd' }} />}
                  </div>
                </Dropdown.Item>
              ))}
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleReorderPipelines}>
                <GripVertical size={14} className="me-2" />
                Reorder pipelines
              </Dropdown.Item>
              <Dropdown.Item>
                <Eye size={14} className="me-2" />
                Pipeline visibility
              </Dropdown.Item>
              <Dropdown.Item>
                <Pencil size={14} className="me-2" />
                Customize deal cards
              </Dropdown.Item>
              <Dropdown.Item>
                <Gear size={14} className="me-2" />
                Pipeline layout settings
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleNewPipeline} style={{ color: '#0d6efd', fontWeight: 500 }}>
                <Plus size={14} className="me-2" />
                New pipeline
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <OverlayTrigger placement="bottom" overlay={<Tooltip id="edit-pipeline">Edit pipeline</Tooltip>}>
            <button
              type="button"
              onClick={handleEditPipeline}
              style={{
                background: '#fff',
                border: '1px solid #dee2e6',
                borderRadius: 4,
                padding: '0.25rem 0.4rem',
                cursor: 'pointer',
                color: '#6c757d',
              }}
            >
              <Pencil size={14} />
            </button>
          </OverlayTrigger>
        </div>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <Card>
          <Table responsive hover size="sm" className="mb-0">
            <thead style={{ backgroundColor: '#f8f9fa', fontSize: '0.75rem' }}>
              <tr>
                <th style={{ width: 40 }}><Form.Check type="checkbox" /></th>
                <th>Title</th>
                <th>Value</th>
                <th>Organization</th>
                <th>Contact person</th>
                <th>Expected close date</th>
                <th>Stage</th>
              </tr>
            </thead>
            <tbody style={{ fontSize: '0.75rem' }}>
              {deals
                .filter((d: Deal) => !searchQuery || (d.name || '').toLowerCase().includes(searchQuery.toLowerCase()) || (d.notes || '').toLowerCase().includes(searchQuery.toLowerCase()))
                .map((deal: Deal) => (
                  <tr key={deal.id}>
                    <td><Form.Check type="checkbox" /></td>
                    <td
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenDealSidebar(deal)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleOpenDealSidebar(deal);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <strong>{deal.name}</strong>
                    </td>
                    <td
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenDealSidebar(deal)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleOpenDealSidebar(deal);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {formatCurrency(deal.value, deal.currency)}
                    </td>
                    <td
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenDealSidebar(deal)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleOpenDealSidebar(deal);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {getDealOrganizationLabel(deal)}
                    </td>
                    <td
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenDealSidebar(deal)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleOpenDealSidebar(deal);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {getDealContactLabel(deal)}
                    </td>
                    <td
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenDealSidebar(deal)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleOpenDealSidebar(deal);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {deal.expectedCloseDate
                        ? toLocaleDateStringInTimezone(
                            deal.expectedCloseDate,
                            'en-US',
                            { month: 'short', day: 'numeric', year: 'numeric' },
                            tenantTimezone
                          )
                        : '—'}
                    </td>
                    <td
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenDealSidebar(deal)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleOpenDealSidebar(deal);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {formatStageName(deal.stage || '')}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </Card>
      )}

      {/* Kanban Board */}
      {viewMode === 'kanban' && (
      <div className="d-flex gap-3" style={{ overflowX: 'auto', minHeight: '600px' }}>
        {dealStages.map((stageObj) => {
          const stage = stageObj.id;
          const stageDeals = dealsByStage[stage] || [];
          const stageTotal = stageDeals.reduce((sum, deal) => {
            return sum + (parseFloat(deal.value) || 0);
          }, 0);

          return (
            <div
              key={stage}
              style={{
                minWidth: '280px',
                width: '280px',
                flexShrink: 0
              }}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(stage)}
            >
              <Card style={{ height: '100%', borderTop: `4px solid ${getStageColor(stage)}` }}>
                <Card.Header style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '0.75rem',
                  borderBottom: '1px solid #dee2e6'
                }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                        {formatStageName(stage)}
                      </h6>
                      <small style={{ fontSize: '0.65rem', opacity: 0.7 }}>
                        {stageDeals.length} deals
                      </small>
                    </div>
                    <Badge bg="secondary" style={{ fontSize: '0.6rem' }}>
                      {formatCurrency(stageTotal.toString())}
                    </Badge>
                  </div>
                </Card.Header>
                <Card.Body style={{ padding: '0.5rem', overflowY: 'auto', maxHeight: '550px' }}>
                  {stageDeals.map((deal: Deal) => (
                    <Card
                      key={deal.id}
                      className="mb-2"
                      draggable
                      onDragStart={() => handleDragStart(deal)}
                      onDragEnd={handleDragEnd}
                      style={{ 
                        cursor: 'move',
                        fontSize: '0.75rem'
                      }}
                    >
                      <Card.Body
                        style={{ padding: '0.75rem' }}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleOpenDealSidebar(deal)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleOpenDealSidebar(deal);
                          }
                        }}
                      >
                        <div className="mb-2">
                          <strong>{deal.name}</strong>
                        </div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <CurrencyDollar size={12} style={{ opacity: 0.6 }} />
                          <span>{formatCurrency(deal.value, deal.currency)}</span>
                        </div>
                        {deal.expectedCloseDate && (
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <Calendar size={12} style={{ opacity: 0.6 }} />
                            <span style={{ fontSize: '0.65rem' }}>
                              {toLocaleDateStringInTimezone(
                                deal.expectedCloseDate,
                                'en-US',
                                { month: 'short', day: 'numeric', year: 'numeric' },
                                tenantTimezone
                              )}
                            </span>
                          </div>
                        )}
                        {deal.probability !== undefined && deal.probability > 0 && (
                          <div className="mb-2">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <small style={{ fontSize: '0.65rem' }}>Probability</small>
                              <small style={{ fontSize: '0.65rem' }}>{deal.probability}%</small>
                            </div>
                            <div className="progress" style={{ height: '4px' }}>
                              <div
                                className="progress-bar"
                                role="progressbar"
                                style={{ width: `${deal.probability}%` }}
                              />
                            </div>
                          </div>
                        )}
                        {deal.tags && deal.tags.length > 0 && (
                          <div className="d-flex gap-1 flex-wrap">
                            {deal.tags.map((tag, idx) => (
                              <Badge key={idx} bg="secondary" style={{ fontSize: '0.6rem', padding: '0.15rem 0.35rem' }}>
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  ))}
                  {stageDeals.length === 0 && (
                    <div className="text-center py-4" style={{ opacity: 0.5 }}>
                      <small>No deals in this stage</small>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          );
        })}
      </div>
      )}

      </div>

      {draggedDeal && (
        <div
          style={{
            position: 'fixed',
            left: '50%',
            bottom: '18px',
            transform: 'translateX(-50%)',
            zIndex: 1100,
            backgroundColor: 'rgba(255, 255, 255, 0.96)',
            border: '1px solid #dee2e6',
            borderRadius: 12,
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
            padding: '0.6rem',
            display: 'flex',
            alignItems: 'stretch',
            gap: '0.5rem',
          }}
          onDragOver={(event) => event.preventDefault()}
        >
          {([
            { id: 'delete', label: 'Delete', hint: 'Remove deal', border: '#dc3545', text: '#dc3545' },
            { id: 'lost', label: 'Lost', hint: 'Mark as lost', border: '#fd7e14', text: '#fd7e14' },
            { id: 'won', label: 'Won', hint: 'Mark won + move to client', border: '#198754', text: '#198754' },
            { id: 'move', label: 'Move', hint: 'Drop on a stage column', border: '#0d6efd', text: '#0d6efd' },
          ] as Array<{ id: DragAction; label: string; hint: string; border: string; text: string }>).map((action) => (
            <div
              key={action.id}
              onDragOver={(event) => handleActionDragOver(event, action.id)}
              onDragLeave={() => setDragActionHover((current) => (current === action.id ? null : current))}
              onDrop={(event) => handleActionDrop(event, action.id)}
              role="button"
              tabIndex={-1}
              style={{
                minWidth: 150,
                padding: '0.55rem 0.65rem',
                borderRadius: 10,
                border: `1px solid ${action.border}`,
                backgroundColor: dragActionHover === action.id ? action.border : '#fff',
                color: dragActionHover === action.id ? '#fff' : action.text,
                fontSize: '0.72rem',
                lineHeight: 1.2,
                userSelect: 'none',
                transition: 'all 120ms ease',
                cursor: 'copy',
              }}
            >
              <div style={{ fontWeight: 700 }}>{action.label}</div>
              <div style={{ marginTop: 2, opacity: 0.9 }}>{action.hint}</div>
            </div>
          ))}
        </div>
      )}

      <Offcanvas
        show={showDealSidebar}
        onHide={handleCloseDealSidebar}
        placement="end"
        style={{
          width: 'calc(100vw - 510px)',
          maxWidth: '1200px',
          minWidth: '800px',
          top: '56px',
          height: 'calc(100vh - 56px)',
          zIndex: 1040,
        }}
      >
        <Offcanvas.Header
          className="position-relative"
          style={{
            borderBottom: '1px solid #dee2e6',
            paddingTop: '0.95rem',
            paddingBottom: '1.05rem',
          }}
        >
          <Offcanvas.Title style={{ flex: 1 }}>
            {selectedDeal && (
              <div>
                <div className="fw-bold" style={{ fontSize: '0.85rem' }}>
                  {selectedDealHeaderTitle}
                </div>
                {selectedDealOrganizationLabel !== '—' && (
                  <div className="text-muted small mt-1" style={{ fontSize: '0.7rem' }}>
                    {selectedDealOrganizationLabel}
                  </div>
                )}
              </div>
            )}
          </Offcanvas.Title>
          <Button
            variant="link"
            className="p-0 position-absolute"
            onClick={handleCloseDealSidebar}
            style={{ top: '1.4rem', right: '1rem', zIndex: 10 }}
            aria-label="Close deal details"
          >
            <X size={20} />
          </Button>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          {selectedDeal && (
            <div className="d-flex h-100" style={{ overflowX: 'hidden' }}>
              <div
                className="crm-sidebar-left-column"
                style={{
                  width: '320px',
                  padding: '1rem',
                  borderRight: '1px solid #dee2e6',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div className="mb-4" style={{ flex: 1 }}>
                  <div className="mb-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h6 className="text-uppercase text-muted small mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>DETAILS</h6>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => setShowAllDealDetailFields((prev) => !prev)}
                      >
                        {showAllDealDetailFields ? 'Hide fields' : 'Show all fields'}
                      </Button>
                    </div>
                    {showAllDealDetailFields && (
                      <div className="mb-2 d-flex align-items-center gap-2" style={{ padding: '0.5rem', borderRadius: '4px' }}>
                        <span style={dealSidebarIconStyle}>
                          <FileText size={14} />
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>Add to sequence</span>
                      </div>
                    )}

                    {renderEditableDealLabelsRow('detailsLabels', showAllDealDetailFields)}
                    {renderDealSidebarField('value', '$ Value', CurrencyDollar, { showAll: showAllDealDetailFields })}
                    {renderDealSidebarField('expectedCloseDate', 'Expected close date', Calendar, { showAll: showAllDealDetailFields })}
                    {renderDealSidebarField('stage', 'Stage', Tag, { showAll: showAllDealDetailFields })}
                    {renderDealSidebarField('businessUnit', 'Business Unit:', Briefcase, { showAll: showAllDealDetailFields })}
                    {renderDealSidebarField('owner', 'Owner', Person, { showAll: showAllDealDetailFields })}
                    {renderDealSidebarField('visibility', 'Visibility', PeopleFill, { showAll: showAllDealDetailFields })}
                    {renderDealSidebarField('sourceOrigin', 'Source origin', ArrowRight, { showAll: showAllDealDetailFields, showInfo: true })}
                    {renderDealSidebarField('sourceChannel', 'Source channel', ArrowRight, { showAll: showAllDealDetailFields })}
                    {renderDealSidebarField('sourceChannelId', 'Source channel ID', Dot, { showAll: showAllDealDetailFields })}
                  </div>

                  <div className="mb-3" style={{ marginTop: '2rem' }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h6 className="text-uppercase text-muted small mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>PERSON</h6>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => setShowAllDealPersonFields((prev) => !prev)}
                      >
                        {showAllDealPersonFields ? 'Hide fields' : 'Show all fields'}
                      </Button>
                    </div>
                    {renderDealSidebarField('name', 'No name', Person, { showAll: showAllDealPersonFields })}
                    {renderDealSidebarField('contactStage', 'Contact Stage', CheckSquare, { showAll: showAllDealPersonFields })}
                    {renderDealSidebarField('email', 'No email', Envelope, { showAll: showAllDealPersonFields })}
                    {renderDealSidebarField('phone', 'No phone', Phone, { showAll: showAllDealPersonFields })}
                  </div>

                  <div className="mb-3" style={{ marginTop: '2rem' }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h6 className="text-uppercase text-muted small mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>ORGANIZATION</h6>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => setShowAllDealOrganizationFields((prev) => !prev)}
                      >
                        {showAllDealOrganizationFields ? 'Hide fields' : 'Show all fields'}
                      </Button>
                    </div>
                    {renderDealSidebarField('organization', 'No organization', BuildingFill, { showAll: showAllDealOrganizationFields })}
                    {renderDealSidebarField('accountStage', 'Account Stage', Briefcase, { showAll: showAllDealOrganizationFields })}
                    {renderDealSidebarField('address', 'Address', GeoAltFill, { showAll: showAllDealOrganizationFields })}
                    {renderDealSidebarField('website', 'Website', Globe2, { showAll: showAllDealOrganizationFields })}
                    {renderDealSidebarField('linkedInProfile', 'LinkedIn profile', Linkedin, { showAll: showAllDealOrganizationFields })}
                    {renderDealSidebarField('numberOfEmployees', 'Number of employees', PeopleFill, { showAll: showAllDealOrganizationFields })}
                    {renderDealSidebarField('annualRevenue', 'Annual revenue', CurrencyDollar, { showAll: showAllDealOrganizationFields })}
                    {renderDealSidebarField('industry', 'Industry', Briefcase, { showAll: showAllDealOrganizationFields })}
                  </div>
                </div>

                <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid #dee2e6' }}>
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleDeleteDealFromSidebar}
                      style={{
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderColor: '#ced4da',
                      }}
                      title="Delete deal"
                    >
                      <Trash2 size={14} />
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSaveDealFromSidebar}
                      disabled={isSavingSidebarDeal || !String(dealSidebarForm.name || '').trim()}
                      style={{ fontSize: '0.7rem', padding: '0.35rem 0.75rem', width: 'auto', borderRadius: '10px' }}
                    >
                      {isSavingSidebarDeal ? <Spinner size="sm" animation="border" /> : 'Save deal'}
                    </Button>
                  </div>
                </div>
              </div>

              <div style={{ flex: 1, padding: '1rem', overflowY: 'auto', overflowX: 'hidden' }}>
                <Tabs
                  activeKey={dealSidebarTab}
                  onSelect={(k) => k && setDealSidebarTab(k as DealSidebarTab)}
                  className="mb-3"
                  style={{ fontSize: '0.75rem' }}
                >
                  <Tab eventKey="notes" title="Notes">
                    <div className="mb-3" style={{ backgroundColor: '#ffffff', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        value={dealSidebarForm.notes}
                        onChange={(e) => handleDealSidebarFieldChange('notes', e.target.value)}
                        placeholder="Take a note, @name..."
                        style={{
                          border: 'none',
                          fontSize: '0.75rem',
                          minHeight: '120px',
                          boxShadow: 'none',
                        }}
                      />
                      <div className="d-flex justify-content-end gap-2 border-top px-2 py-2" style={{ backgroundColor: '#f8f9fa' }}>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleDealSidebarFieldChange('notes', String(selectedDeal.notes || ''))}
                          disabled={isSavingSidebarDeal}
                          style={{ fontSize: '0.7rem' }}
                        >
                          Reset
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleSaveDealFromSidebar}
                          disabled={isSavingSidebarDeal}
                          style={{ fontSize: '0.7rem' }}
                        >
                          {isSavingSidebarDeal ? <Spinner size="sm" animation="border" /> : 'Save note'}
                        </Button>
                      </div>
                    </div>
                  </Tab>

                  <Tab eventKey="activity" title="Activity">
                    <Row className="g-3">
                      <Col md={8}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: '0.75rem' }}>Title</Form.Label>
                          <Form.Control
                            size="sm"
                            value={dealSidebarForm.name}
                            onChange={(e) => handleDealSidebarFieldChange('name', e.target.value)}
                            placeholder="Deal name"
                            style={{ fontSize: '0.75rem' }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: '0.75rem' }}>Probability</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            min={0}
                            max={100}
                            value={dealSidebarForm.probability}
                            onChange={(e) => handleDealSidebarFieldChange('probability', Number(e.target.value) || 0)}
                            style={{ fontSize: '0.75rem' }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: '0.75rem' }}>Stage</Form.Label>
                          <Form.Select
                            size="sm"
                            value={dealSidebarForm.stage}
                            onChange={(e) => handleDealSidebarFieldChange('stage', e.target.value)}
                            style={{ fontSize: '0.75rem' }}
                          >
                            {dealSidebarStageOptions.map((stage) => (
                              <option key={stage.id} value={stage.id}>{stage.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: '0.75rem' }}>Expected close date</Form.Label>
                          <Form.Control
                            size="sm"
                            type="date"
                            value={dealSidebarForm.expectedCloseDate}
                            onChange={(e) => handleDealSidebarFieldChange('expectedCloseDate', e.target.value)}
                            style={{ fontSize: '0.75rem' }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={8}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: '0.75rem' }}>Value</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            min={0}
                            value={dealSidebarForm.value}
                            onChange={(e) => handleDealSidebarFieldChange('value', e.target.value)}
                            placeholder="0"
                            style={{ fontSize: '0.75rem' }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: '0.75rem' }}>Currency</Form.Label>
                          <Form.Select
                            size="sm"
                            value={dealSidebarForm.currency}
                            onChange={(e) => handleDealSidebarFieldChange('currency', e.target.value)}
                            style={{ fontSize: '0.75rem' }}
                          >
                            <option value="US Dollar (USD)">USD</option>
                            <option value="EUR">EUR</option>
                            <option value="GBP">GBP</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: '0.75rem' }}>Linked client</Form.Label>
                          <Form.Select
                            size="sm"
                            value={dealSidebarForm.clientId}
                            onChange={(e) => handleDealSidebarFieldChange('clientId', e.target.value)}
                            style={{ fontSize: '0.75rem' }}
                          >
                            <option value="">None</option>
                            {clients.map((client: any) => (
                              <option key={client.id} value={client.id}>
                                {String(client.name || client.organization || 'Untitled client')}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end mt-3">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSaveDealFromSidebar}
                        disabled={isSavingSidebarDeal || !String(dealSidebarForm.name || '').trim()}
                        style={{ fontSize: '0.7rem' }}
                      >
                        {isSavingSidebarDeal ? <Spinner size="sm" animation="border" /> : 'Save changes'}
                      </Button>
                    </div>
                  </Tab>

                  <Tab eventKey="email" title="Email">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h6 className="small mb-0" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                        Linked threads
                      </h6>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => refetchDealEmailThreads()}
                        disabled={!selectedDealEmailAddress || isRefreshingDealEmailThreads}
                        style={{ fontSize: '0.65rem', padding: '0.2rem 0.5rem' }}
                      >
                        {isRefreshingDealEmailThreads ? <Spinner size="sm" animation="border" /> : 'Refresh'}
                      </Button>
                    </div>
                    {!selectedDealEmailAddress ? (
                      <p className="text-muted small" style={{ fontSize: '0.7rem' }}>
                        Link a contact or lead with an email address to this deal to link Unibox threads.
                      </p>
                    ) : isLoadingDealEmailThreads ? (
                      <div className="d-flex align-items-center gap-2 text-muted small" style={{ fontSize: '0.7rem' }}>
                        <Spinner size="sm" animation="border" />
                        Loading email threads...
                      </div>
                    ) : dealEmailThreadsError ? (
                      <Alert variant="danger" className="py-2 px-3 mb-0" style={{ fontSize: '0.7rem' }}>
                        Failed to load email threads.
                      </Alert>
                    ) : dealEmailThreads.length === 0 ? (
                      <p className="text-muted small mb-0" style={{ fontSize: '0.7rem' }}>
                        No email threads found for {selectedDealEmailAddress}.
                      </p>
                    ) : (
                      <ListGroup variant="flush">
                        {dealEmailThreads.map((thread) => (
                          <ListGroup.Item key={thread.id} className="px-0 py-2" style={{ fontSize: '0.75rem' }}>
                            <div className="d-flex align-items-start gap-2">
                              <Envelope size={14} className="mt-1" style={{ color: '#6c757d' }} />
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-start justify-content-between gap-2">
                                  <div className="fw-semibold" style={{ fontSize: '0.75rem' }}>
                                    {thread.subject}
                                  </div>
                                  <div className="d-flex align-items-center gap-1">
                                    {thread.unreadCount > 0 && (
                                      <Badge bg="primary" style={{ fontSize: '0.6rem' }}>
                                        {thread.unreadCount} unread
                                      </Badge>
                                    )}
                                    {thread.messageCount > 1 && (
                                      <Badge bg="secondary" style={{ fontSize: '0.6rem' }}>
                                        {thread.messageCount} msgs
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <div className="text-muted small mt-1" style={{ fontSize: '0.7rem' }}>
                                  {formatDate(thread.latestDate)} · {thread.latestFrom || 'Unknown sender'}
                                </div>
                                <div className="mt-2 d-flex flex-column gap-2">
                                  {thread.messages.map((message) => (
                                    <div key={`${thread.id}-${message.id}`} className="border-start ps-2">
                                      <div className="text-muted small" style={{ fontSize: '0.67rem' }}>
                                        {formatDate(message.date)} · {message.from || 'Unknown sender'}
                                      </div>
                                      <div className="small mt-1" style={{ fontSize: '0.7rem', lineHeight: 1.35 }}>
                                        {message.preview || 'No preview available.'}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </Tab>

                  <Tab eventKey="files" title="Files">
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      No files attached.
                    </div>
                  </Tab>
                </Tabs>
              </div>
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {/* Add Deal Modal */}
      <Modal show={showAddDealModal} onHide={() => setShowAddDealModal(false)} size="lg">
        <Modal.Header style={{ fontSize: '1rem', padding: '0.75rem 1rem' }}>
          <Modal.Title>Add deal</Modal.Title>
          <Button variant="link" onClick={() => setShowAddDealModal(false)} style={{ padding: 0, fontSize: '18px' }}>
            <X />
          </Button>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '0.75rem', padding: '1rem' }}>
          <Row>
            <Col md={7}>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.75rem' }}>Contact person</Form.Label>
                <div style={{ position: 'relative' }}>
                  <InputGroup size="sm">
                    <InputGroup.Text style={{ backgroundColor: '#f8f9fa' }}><Person size={14} /></InputGroup.Text>
                    <Form.Control
                      value={contactDisplayLabel || contactSearch}
                      onChange={(e) => {
                        const v = e.target.value;
                        setContactSearch(v);
                        setShowContactDropdown(true);
                        if (formData.contactId || formData.leadId) {
                          setFormData((p) => ({ ...p, contactId: '', leadId: '', contactName: v }));
                        } else {
                          setFormData((p) => ({ ...p, contactName: v }));
                        }
                      }}
                      onFocus={() => setShowContactDropdown(true)}
                      onBlur={() => setTimeout(() => setShowContactDropdown(false), 200)}
                      placeholder="Search contacts or leads, or type new"
                      style={{ fontSize: '0.75rem' }}
                    />
                    {(formData.contactId || formData.leadId || formData.contactName) && (
                      <Button variant="link" size="sm" style={{ padding: '0.25rem 0.5rem', color: '#6c757d' }}
                        onClick={() => {
                          setFormData((p) => ({ ...p, contactId: '', leadId: '', contactName: '' }));
                          setContactSearch('');
                        }}>
                        <XLg size={12} />
                      </Button>
                    )}
                  </InputGroup>
                  {showContactDropdown && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                      {filteredContactsAndLeads.length === 0 ? (
                        <div
                          style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: contactSearch.trim() ? '#0d6efd' : '#6c757d', cursor: contactSearch.trim() ? 'pointer' : 'default' }}
                          onClick={contactSearch.trim() ? () => {
                            setFormData((p) => ({ ...p, contactName: contactSearch.trim(), contactId: '', leadId: '' }));
                            setContactSearch('');
                            setShowContactDropdown(false);
                          } : undefined}
                          onMouseDown={contactSearch.trim() ? (e) => e.preventDefault() : undefined}
                        >
                          {contactSearch.trim() ? `Use "${contactSearch}" as new contact` : 'No contacts or leads found'}
                        </div>
                      ) : (
                        filteredContactsAndLeads.map((item) => (
                          <div
                            key={`${item.type}-${item.id}`}
                            onClick={() => {
                              if (item.type === 'contact') {
                                setFormData((p) => ({ ...p, contactId: item.id, leadId: '', contactName: '' }));
                              } else {
                                setFormData((p) => ({ ...p, leadId: item.id, contactId: '', contactName: '' }));
                              }
                              setContactSearch('');
                              setShowContactDropdown(false);
                            }}
                            style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {item.type === 'contact' ? '👤 ' : '📋 '}{item.label}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.75rem' }}>Organization</Form.Label>
                <div style={{ position: 'relative' }}>
                  <InputGroup size="sm">
                    <InputGroup.Text style={{ backgroundColor: '#f8f9fa' }}><Building size={14} /></InputGroup.Text>
                    <Form.Control
                      value={orgDisplayLabel || orgSearch}
                      onChange={(e) => {
                        setOrgSearch(e.target.value);
                        setShowOrgDropdown(true);
                        if (formData.clientId) setFormData((p) => ({ ...p, clientId: '' }));
                      }}
                      onFocus={() => setShowOrgDropdown(true)}
                      onBlur={() => setTimeout(() => setShowOrgDropdown(false), 200)}
                      placeholder="Search organizations"
                      style={{ fontSize: '0.75rem' }}
                    />
                    {(formData.clientId || orgSearch) && (
                      <Button variant="link" size="sm" style={{ padding: '0.25rem 0.5rem', color: '#6c757d' }}
                        onClick={() => { setFormData((p) => ({ ...p, clientId: '' })); setOrgSearch(''); }}>
                        <XLg size={12} />
                      </Button>
                    )}
                  </InputGroup>
                  {showOrgDropdown && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                      {filteredOrganizations.length === 0 ? (
                        <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6c757d' }}>No organizations found</div>
                      ) : (
                        filteredOrganizations.map((item) => (
                          <div
                            key={`${item.type}-${item.id}`}
                            onClick={() => {
                              if (item.type === 'client') {
                                setFormData((p) => ({ ...p, clientId: item.id }));
                                setOrgSearch('');
                              } else {
                                setFormData((p) => ({ ...p, clientId: '' }));
                                setOrgSearch(item.label);
                              }
                              setShowOrgDropdown(false);
                            }}
                            style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {item.label}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.75rem' }}>Title</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  placeholder="Deal name"
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.75rem' }}>Value</Form.Label>
                <InputGroup size="sm">
                  <Form.Control
                    type="number"
                    value={formData.value}
                    onChange={(e) => handleFormChange('value', e.target.value)}
                    placeholder="0"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  />
                  <Form.Select
                    size="sm"
                    value={formData.currency}
                    onChange={(e) => handleFormChange('currency', e.target.value)}
                    style={{ fontSize: '0.75rem', maxWidth: 140 }}
                  >
                    <option value="US Dollar (USD)">US Dollar (USD)</option>
                    <option value="EUR">Euro (EUR)</option>
                    <option value="GBP">British Pound (GBP)</option>
                  </Form.Select>
                </InputGroup>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.75rem' }}>Pipeline</Form.Label>
                <Form.Select
                  size="sm"
                  value={addDealPipelineId || selectedPipelineId || pipelines[0]?.id}
                  onChange={(e) => {
                    const id = e.target.value;
                    setAddDealPipelineId(id);
                    const pipe = pipelines.find((p) => p.id === id);
                    if (pipe?.stages?.[0]) setFormData((p) => ({ ...p, stage: pipe.stages[0].id }));
                  }}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  {pipelines.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.75rem' }}>Pipeline stage</Form.Label>
                <div className="d-flex flex-wrap gap-1 mb-1">
                  {addDealStages.map((s) => (
                    <Button
                      key={s.id}
                      variant={formData.stage === s.id ? 'success' : 'outline-secondary'}
                      size="sm"
                      onClick={() => handleFormChange('stage', s.id)}
                      style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}
                    >
                      {s.name}
                    </Button>
                  ))}
                </div>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.75rem' }}>Probability (%)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="100"
                  size="sm"
                  value={formData.probability}
                  onChange={(e) => handleFormChange('probability', parseInt(e.target.value) || 0)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', maxWidth: 80 }}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.75rem' }}>Expected close date</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={formData.expectedCloseDate}
                  onChange={(e) => handleFormChange('expectedCloseDate', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', maxWidth: 160 }}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.75rem' }}>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  size="sm"
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={5}>
              <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6c757d', marginBottom: '0.5rem' }}>PERSON</div>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.75rem' }}>Phone</Form.Label>
                <InputGroup size="sm">
                  <InputGroup.Text style={{ backgroundColor: '#f8f9fa' }}><Telephone size={14} /></InputGroup.Text>
                  <Form.Control
                    value={formData.personPhone}
                    onChange={(e) => handleFormChange('personPhone', e.target.value)}
                    placeholder="Work"
                    style={{ fontSize: '0.75rem' }}
                  />
                  <Form.Select size="sm" style={{ maxWidth: 90, fontSize: '0.7rem' }}>
                    <option>Work</option>
                  </Form.Select>
                </InputGroup>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.75rem' }}>Email</Form.Label>
                <InputGroup size="sm">
                  <InputGroup.Text style={{ backgroundColor: '#f8f9fa' }}><Envelope size={14} /></InputGroup.Text>
                  <Form.Control
                    type="email"
                    value={formData.personEmail}
                    onChange={(e) => handleFormChange('personEmail', e.target.value)}
                    placeholder="Work"
                    style={{ fontSize: '0.75rem' }}
                  />
                  <Form.Select size="sm" style={{ maxWidth: 90, fontSize: '0.7rem' }}>
                    <option>Work</option>
                  </Form.Select>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer style={{ fontSize: '0.75rem', padding: '0.75rem 1rem' }}>
          <div className="d-flex justify-content-between align-items-center w-100">
            <span style={{ fontSize: '0.75rem' }}>
              {deals.length}/unlimited
            </span>
            <div className="d-flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => setShowAddDealModal(false)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                size="sm" 
                onClick={handleSaveDeal}
                disabled={isCreatingDeal || !formData.name}
              >
                {isCreatingDeal ? <Spinner size="sm" animation="border" /> : 'Save'}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Edit Pipeline Modal */}
      <Modal show={showEditPipelineModal} onHide={() => { setShowEditPipelineModal(false); setEditingPipeline(null); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isNewPipeline ? 'New pipeline' : 'Edit pipeline'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingPipeline && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Pipeline name</Form.Label>
                <Form.Control
                  value={editingPipeline.name}
                  onChange={(e) => setEditingPipeline({ ...editingPipeline, name: e.target.value })}
                  placeholder="Pipeline name"
                />
              </Form.Group>
              <div className="d-flex align-items-center gap-2 mb-3">
                <Form.Check
                  type="switch"
                  id="dealProbability"
                  label="Deal probability"
                  checked={editingPipeline.dealProbability}
                  onChange={(e) => setEditingPipeline({ ...editingPipeline, dealProbability: e.target.checked })}
                />
                <InfoCircle size={14} style={{ color: '#6c757d' }} />
              </div>
              <div className="d-flex gap-2 flex-wrap" style={{ overflowX: 'auto' }}>
                {editingPipeline.stages.map((stage, idx) => (
                  <Card key={stage.id} style={{ minWidth: 200, maxWidth: 220 }}>
                    <Card.Body style={{ padding: '0.75rem', fontSize: '0.8rem' }}>
                      <div className="mb-2 fw-bold">{stage.name}</div>
                      <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '0.7rem' }}>Name</Form.Label>
                        <Form.Control
                          size="sm"
                          value={stage.name}
                          onChange={(e) => {
                            const next = [...editingPipeline.stages];
                            next[idx] = { ...next[idx], name: e.target.value };
                            setEditingPipeline({ ...editingPipeline, stages: next });
                          }}
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '0.7rem' }}>Probability</Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          max={100}
                          size="sm"
                          value={stage.probability}
                          onChange={(e) => {
                            const next = [...editingPipeline.stages];
                            next[idx] = { ...next[idx], probability: parseInt(e.target.value) || 0 };
                            setEditingPipeline({ ...editingPipeline, stages: next });
                          }}
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Check
                          type="switch"
                          id={`rotting-${stage.id}`}
                          label="Rotting in (days)"
                          checked={typeof stage.rottingDays === 'number'}
                          onChange={(e) => {
                            const next = [...editingPipeline.stages];
                            next[idx] = { ...next[idx], rottingDays: e.target.checked ? 30 : undefined };
                            setEditingPipeline({ ...editingPipeline, stages: next });
                          }}
                        />
                      </Form.Group>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="w-100"
                        onClick={() => {
                          if (editingPipeline.stages.length <= 1) return;
                          const next = editingPipeline.stages.filter((_, i) => i !== idx);
                          setEditingPipeline({ ...editingPipeline, stages: next });
                        }}
                        disabled={editingPipeline.stages.length <= 1}
                      >
                        <Trash2 size={12} className="me-1" />
                        Delete Stage
                      </Button>
                    </Card.Body>
                  </Card>
                ))}
              </div>
              <Button
                variant="outline-primary"
                size="sm"
                className="mt-2"
                onClick={() => {
                  const id = `stage-${Date.now()}`;
                  setEditingPipeline({
                    ...editingPipeline,
                    stages: [...editingPipeline.stages, { id, name: 'New Stage', probability: 50 }],
                  });
                }}
              >
                <Plus size={12} className="me-1" />
                Add stage
              </Button>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowEditPipelineModal(false); setEditingPipeline(null); }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveEditPipeline} disabled={!editingPipeline?.name?.trim()}>
            Save changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reorder Pipelines Modal */}
      <Modal show={showReorderModal} onHide={() => setShowReorderModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reorder pipelines</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '1rem' }}>
            Changing the order of the pipelines will change the order for all users.
          </p>
          <div className="d-flex flex-column gap-2">
            {reorderPipelines.map((p, index) => (
              <div
                key={p.id}
                className="d-flex align-items-center gap-2 p-2"
                style={{ border: '1px solid #dee2e6', borderRadius: 6 }}
              >
                <div className="d-flex flex-column gap-0">
                  <button
                    type="button"
                    onClick={() => handleMoveReorderPipeline(index, 'up')}
                    disabled={index === 0}
                    style={{ background: 'none', border: 'none', padding: 2, cursor: index === 0 ? 'not-allowed' : 'pointer' }}
                  >
                    <ArrowUp size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveReorderPipeline(index, 'down')}
                    disabled={index === reorderPipelines.length - 1}
                    style={{ background: 'none', border: 'none', padding: 2, cursor: index === reorderPipelines.length - 1 ? 'not-allowed' : 'pointer' }}
                  >
                    <ArrowDown size={12} />
                  </button>
                </div>
                <GripVertical size={16} style={{ color: '#adb5bd' }} />
                <span style={{ flex: 1 }}>{p.name}</span>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReorderModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveReorderPipelines}>Save</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Deals;
